import {
	useGetAPI, useDeleteAPI, usePatchAPI, usePostAPI, usePutAPI
} from '@api/useAPI';
import { XCustomerID, SID, CID, authenticatedUser } from '@state';
import { useState, useCallback } from 'react';
import {
	ITypeSense,
	TBillingInvoiceStatus,
	ShipperCarrierSearch,
	TInvoices,
	TRecheckInvoices,
	TIssueAndClaimTableRow,
	TIssueAndClaimItem,
	TClaimItem,
	TAccountable,
	TSubClaim,
	TTruckType,
	TTruckSize,
	TMarketPlaces,
	TMarketPlaceHoliday,
	TRule,
	THolidays,
	TShipperList,
	TNonRCTIInvoices,
	TNonRCTIInvoiceDetails,
	TInvoiceAction,
	TInvoiceMinorAdjustmentPayload,
	TCarrier,
	TFuelLevyTariffForCost,
	TNonRCTIInvoiceShipment,
	TMatchOrRejectShipmentPayload,
	TInvoiceHistory,
	TInvoiceCommentPayload,
	TShipmentEOS,
	TSuperDock,
	AJAXError,
	TEOSshipment,
	XDockAddress,
	THarmonyResponse,
	TRateCard,
	TCargoAdjustmentResponse,
	TNewClaimPayload,
	TInvoiceDownload,
	TPricingSummaryTableRow,
	TRegisteredShipper,
	PricePreviewResponse,
	APICallProps,
	TRegisteredCarrier,
	TAdminTeamMember,
	TCarrierBlock,
	TCarrierBlocksResponse,
	TBlockReasonOption,
	TCarrierBlockCheckResponse,
	TFinanceApprovalRow,
	TIssueAndClaimAccountable,
	TIssueAndClaimStatuses,
	TInvoiceDetails,
	TBillingDataResponse,
	TAdminCompanyDetails,
	TAdminTeamResponse,
	TPriceAdjustment,
	TUserBrief,
	TRegionAddressItem,
	TGMapRegion,
	TGMapPostcodePolygon,
	TRegionBase,
	IRegionItem,
	TShipmentCancellationReasons,
	NotQuotingPayloadAPICallProps,
	TShipperListOverview,
	TShipperTagResponse,
	TShipperTag,
	QuotesResponse,
	TActiveShipmentPaginator,
	ShipperApprovals,
	CancellationReason,
	TUnactivatedCarrier,
	TActiveManifest,
	TToken,
	TCarrierBlockOptions,
	TABNResponse,
	TEmailCheck,
	ETAReason,
	TMissedTargetOptionsResponse,
	TMissedTargetUpdateResponse,
	TShipmentMissesGroup,
	TDifotReason,
	TShipmentNote,
	Link,
	TShipmentOrder,
	TFuelLevy,
	TFuelLevyTariff,
	TCarrierUnassignReason,
	TShipmentHistory,
	TShipmentDocs,
	AllocateSearchCarrierResult,
	TPrice,
	TCarrierCostPayload,
	TShipmentSearchResult,
	TDataGridResponse,
	TTransportShipment,
	TDocument,
	TPodToBeAssigned,
} from '@types';
import { useRecoilValue } from 'recoil';

/* 	SHIPPER/CARRIER Lookup */

export const useGetShipper = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({ endpoint: '/api/admin/{1}/overview/{2}?search={3}' });

	const triggerCallAPI = (type: 'shipper', search: string, isOnboard: string = 'search') => {
		callAPI({ routes: [type, isOnboard, encodeURIComponent(search)] });
	}

	return {
		loadingShippers: isLoading,
		shippers: data as ShipperCarrierSearch,
		getShipperError: error,
		callGetShippers: triggerCallAPI
	}
}
export const useGetCarrier = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({ endpoint: '/api/admin/{1}/overview/{2}?q={3}&activeOnly={4}&shipperId={5}&allocatableOnly={6}' });

	const triggerCallAPI = (type: 'carrier', search: string, isOnboard: string = 'search', activeOnly: boolean = false, shipperId?: number, allocatableOnly: boolean = false) => {
		callAPI({ routes: [type, isOnboard, encodeURIComponent(search), `${activeOnly}`, shipperId ? `${shipperId}` : '', `${allocatableOnly}`] });
	}

	return {
		loadingCarriers: isLoading,
		carriers: data as ShipperCarrierSearch,
		getCarrierError: error,
		callGetCarriers: triggerCallAPI
	}
}

/* 	ADMIN -> FULFILMENT -> SUPERDOCK */

export const useCreateSuperDock = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = usePostAPI({ endpoint: '/api/v2/super-dock' });

	return {
		loadingSuperDock: isLoading,
		superdock: data?.data as TSuperDock,
		createSuperDockError: error as AJAXError,
		callCreateSuperdock: callAPI
	}
}

export const useGetSuperDocks = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({ endpoint: '/api/v2/super-dock' });

	return {
		loadingSuperDock: isLoading,
		superdocks: data as {
			data: TSuperDock[];
			perPage: number;
			totalRows: number;
			currentPage: number;
		},
		getSuperDocksError: error as AJAXError,
		callGetSuperdocks: callAPI as (props?: APICallProps) => Promise<{
			data: {
				data: TSuperDock[];
				perPage: number;
				totalRows: number;
				currentPage: number;
			} | null;
			error: AJAXError | null
		}>
	}
}

export const useGetSuperDock = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({ endpoint: '/api/v2/super-dock/{1}' });

	return {
		loadingSuperDock: isLoading,
		superdock: data?.data as TSuperDock,
		getSuperDockError: error as AJAXError,
		callGetSuperdock: callAPI
	}
}

export const useUpdateSuperDock = () => {
	const {
		isLoading,
		data,
		error,
		clearError,
		callAPI
	} = usePatchAPI({ endpoint: '/api/v2/super-dock/{1}' });

	return {
		loadingUpdateSuperDock: isLoading,
		updateSuperdock: data?.data as TSuperDock,
		updateSuperDockError: error as AJAXError,
		clearError: clearError,
		callUpdateSuperdock: callAPI
	}
}

export const useDeleteSuperDocks = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = useDeleteAPI({ endpoint: '/api/v2/super-dock/{1}' });

	return {
		loadingDeleteSuperDock: isLoading,
		deletedSuperdocks: data as { success: boolean },
		deleteSuperDockError: error as AJAXError,
		callDeleteSuperdock: callAPI
	}
}

export const useDeleteSuperDockShipment = () => {
	const {
		isLoading,
		data,
		error,
		clearError,
		callAPI
	} = useDeleteAPI({ endpoint: '/api/v2/super-dock/{1}/original-shipment/{2}' });

	const triggerAPI = (props: { superDockID: string; shipmentID: string }) => {
		const {
			superDockID,
			shipmentID
		} = props;
		callAPI({ routes: [superDockID, shipmentID] });
	}

	return {
		loadingDeleteSDShipment: isLoading,
		deletedSDShipments: data as { type: string, message: string },
		deleteSDShipmentsError: error as AJAXError,
		clearError: clearError,
		callDeleteSDShipments: triggerAPI
	}
}

export const useGetSimilarShipments = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({ endpoint: '/api/v2/super-dock/{1}/search-similar-shipments' });

	return {
		loadingSimilarShipments: isLoading,
		similarShipments: data?.data as TEOSshipment[],
		getSimilarShipmentsError: error as AJAXError,
		callGetSimilarShipments: callAPI
	}
}

export const useGetXDockAddress = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({ endpoint: '/api/v2/super-dock/cross-dock-address' });

	return {
		loadingXDockAddresses: isLoading,
		XDockAddresses: data?.data as XDockAddress[],
		getXDockAddressesError: error as AJAXError,
		callGetXDockAddresses: callAPI
	}
}

export const usePostForAllocatePrice = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = usePostAPI({ endpoint: '/api/v2/super-dock/price' });

	return {
		loadingAllocationPrice: isLoading,
		allocationPrice: data as { 'status': string; data: TRateCard | null; fuelLevyTariff: TFuelLevyTariffForCost | null },
		allocationPriceError: error as AJAXError,
		callPostForAllocationPrice: callAPI
	}
}

export const usePostForConsolidateDiscount = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = usePostAPI({ endpoint: '/api/v2/super-dock/discount' });

	return {
		loadingConsolidationDiscount: isLoading,
		consolidationDiscount: data as { 'status': string; data: { discounts: TPricingSummaryTableRow[] } | null },
		consolidationDiscountError: error as AJAXError,
		callPostForConsolidateDiscount: callAPI
	}
}

export const useGetHarmonyResults = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({ endpoint: '/api/admin/fulfilment/carrier/search' });

	return {
		loadingHarmonyResults: isLoading,
		harmonyResults: data as THarmonyResponse,
		getHarmonyResultsError: error as AJAXError,
		callGetHarmonyResults: callAPI
	}
}

export const useSearchAllCarriers = () => {
	const {
		isLoading,
		error,
		data,
		callAPI
	} = useGetAPI({ endpoint: '/api/admin/fulfilment/carriers/search' });

	/* shipper_ids is what makes the endpoint mark carriers that shipper has blocked. */
	const triggerAPI = async (filter: { search?: string; customer_external_id?: string }, shipperId?: number) => {
		return await callAPI({
			params: {
				...(filter.search && { 'filter[search]': filter.search }),
				...(filter.customer_external_id && { 'filter[customer_external_id]': filter.customer_external_id }),
				...(shipperId && { shipper_ids: String(shipperId) }),
			}
		});
	}

	return {
		loadingCarrierSearch: isLoading,
		carrierSearchError: error as AJAXError,
		carrierData: data?.data as AllocateSearchCarrierResult[],
		searchAllCarriers: triggerAPI as (
			filter: { search?: string; customer_external_id?: string },
			shipperId?: number
		) => Promise<{
			data: { data: AllocateSearchCarrierResult[]; };
			error: AJAXError | null
		}>
	}
}

export const useFetchCarrierCost = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = usePostAPI({
		endpoint: '/api/prices',
		domain: 'ShipmentAPI',
		avoidCache: true,
	});

	const triggerCallAPI = async (customerExternalId: string, payload: TCarrierCostPayload) => {
		return await callAPI({
			payload,
			headers: {
				'X-Customer-Id': customerExternalId,
				'X-AvoidCache': 'avoid'
			},
		});
	}

	return {
		fetchingCarrierCost: isLoading,
		carrierCostData: data?.data as TPrice[],
		fetchCarrierCostError: error as AJAXError,
		fetchCarrierCost: triggerCallAPI as (customerExternalId: string, payload: TCarrierCostPayload) => Promise<{
			data: { data: TPrice[]; };
			error: AJAXError | null
		}>
	};
};

export const useFetchFuelLevyTariff = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({
		endpoint: '/api/fuel-levy/global/carrier/tariff/{1}',
		domain: 'ShipmentAPI',
		avoidCache: true,
	});

	const triggerCallAPI = async (pickupDate: string) => {
		return await callAPI({ routes: [pickupDate] });
	}

	return {
		fetchingFuelLevyTariff: isLoading,
		fuelLevyTariffData: data?.data as TFuelLevyTariffForCost,
		fuelLevyTariffError: error as AJAXError,
		fetchFuelLevyTariff: triggerCallAPI as (pickupDate: string) => Promise<{
			data: { data: TFuelLevyTariffForCost; };
			error: AJAXError | null
		}>,
	};
};

export const useGetHarmonyRecommendationResults = () => {
	const {
		isLoading,
		data,
		error,
		clearError,
		callAPI
	} = useGetAPI({ endpoint: '/api/admin/fulfilment/carrier-recommendation/search?pickup_suburb={1}&pickup_postcode={2}&pickup_state={3}&dropoff_suburb={4}&dropoff_postcode={5}&dropoff_state={6}&cargo_type={7}&truck_size={8}&truck_type={9}&temperature={10}' });

	const apiCall = (
		pickupSuburb: string,
		pickupPostcode: string,
		pickupState: string,
		dropoffSuburb: string,
		dropoffPostcode: string,
		dropoffState: string,
		cargoType: string,
		truckSize: string,
		truckType: string,
		temperature: string
	) => {
		callAPI({
			routes: [
				pickupSuburb,
				pickupPostcode,
				pickupState,
				dropoffSuburb,
				dropoffPostcode,
				dropoffState,
				cargoType,
				truckSize,
				truckType,
				temperature
			]
		})
	}

	return {
		loadingHarmonyResults: isLoading,
		harmonyResults: data as THarmonyResponse,
		getHarmonyResultsError: error as AJAXError,
		callGetHarmonyResults: apiCall,
		clearHarmonyResultsError: clearError
	}
}

/* 	ADMIN -> INBOX -> QUOTES */

export const useGetQuotes = () => {
	const endpoint = '/api/admin/inbox/quotes'
	const {
		isLoading,
		data,
		error,
		clearError,
		callAPI
	} = useGetAPI({ endpoint });

	return {
		loadingQuotes: isLoading,
		quotes: data,
		getQuotesError: error,
		clearGetQuotesError: clearError,
		callGetQuotes: callAPI,
		apiEndpoint: endpoint
	}
}

export const useFilterQuotes = () => {
	const {
		isLoading,
		data,
		error,
		clearError,
		callAPI
	} = useGetAPI({ endpoint: '/api/admin/inbox/quotes?{1}' });

	const triggerAPI = (query: string) => {
		callAPI({ routes: [query] });
	}

	return {
		loadingFilterQuotes: isLoading,
		filter: data,
		getFilteredQuotesError: error,
		clearGetFilterQuotesError: clearError,
		callGetFilterQuotes: triggerAPI
	}
}

export const useDeleteQuote = () => {
	const {
		isLoading,
		data,
		error,
		clearError,
		callAPI
	} = useDeleteAPI({ endpoint: `/api/admin/inbox/quote/{1}` });

	const triggerAPI = async (id: string, payload: {
		reason_id: string;
		cancellation_note: string;
	}) => {
		return await callAPI({ routes: [id], payload });
	}

	return {
		loadingDeleteQuote: isLoading,
		deletedQuote: data,
		getDeleteQuoteError: error,
		clearDeleteQuoteError: clearError,
		callDeleteQuote: triggerAPI
	}
}

/* 	ADMIN -> INBOX -> NEW SHIPPERS */

export const useGetNewShippers = () => {
	const endpoint = '/api/admin/inbox/shippers';
	const {
		isLoading,
		data,
		error,
		clearError,
		clearData,
		callAPI,
	} = useGetAPI({ endpoint });

	return {
		loadingNewShippers: isLoading,
		newShippers: data,
		getNewShippersError: error,
		clearNewShipperError: clearError,
		clearNewShipperData: clearData,
		callGetNewShippers: callAPI,
		apiEndpoint: endpoint,
	}
}

export const useFilterNewShippers = () => {
	const {
		isLoading,
		data,
		error,
		clearError,
		clearData,
		callAPI
	} = useGetAPI({ endpoint: '/api/admin/inbox/shippers?{1}' });

	const triggerAPI = (query: string) => {
		callAPI({ routes: [query] });
	}

	return {
		loadingFilterNewShippers: isLoading,
		filter: data,
		getFilteredNewShippersError: error,
		clearFilterShipperError: clearError,
		clearFilterShipperData: clearData,
		callGetFilterNewShippers: triggerAPI
	}
}

export const useActivateShipper = () => {
	const {
		isLoading,
		data,
		error,
		clearError,
		clearData,
		callAPI
	} = usePatchAPI({ endpoint: '/api/admin/shipper/{1}/shipper-activate' });

	return {
		loadingActivateShipper: isLoading,
		activateShipper: data as { type: string; message: string },
		getActivateShipperError: error as AJAXError,
		clearActivateShipperError: clearError,
		clearActivateShipperData: clearData,
		callActivateShipper: callAPI as (props?: APICallProps) => Promise<{
			data: { type: string; message: string };
			error: AJAXError | null
		}>
	}
}

export const useRejectShipper = () => {
	const {
		isLoading,
		data,
		error,
		clearError,
		clearData,
		callAPI
	} = usePatchAPI({ endpoint: '/api/admin/shipper/reject-shipper' });

	return {
		loadingRejectShipper: isLoading,
		rejectShipper: data as { type: string; message: string },
		getRejectShipperError: error as AJAXError,
		clearRejectShipperError: clearError,
		clearRejectShipperData: clearData,
		callRejectShipper: callAPI as (props?: APICallProps) => Promise<{
			data: { type: string; message: string };
			error: AJAXError | null
		}>
	}
}

export const useDeleteShipper = () => {
	const {
		isLoading,
		data,
		error,
		clearError,
		clearData,
		callAPI
	} = usePatchAPI({ endpoint: '/api/admin/shipper/delete-shipper' });

	return {
		loadingDeleteShipper: isLoading,
		deleteShipper: data as { type: string; message: string },
		getDeleteShipperError: error as AJAXError,
		clearDeleteShipperError: clearError,
		clearDeleteShipperData: clearData,
		callDeleteShipper: callAPI as (props?: APICallProps) => Promise<{
			data: { type: string; message: string };
			error: AJAXError | null
		}>
	}
}

/* 	ADMIN -> INBOX -> NEW CARRIERS */

export const useGetNewCarriers = () => {
	const endpoint = '/api/admin/inbox/carriers';
	const {
		isLoading,
		data,
		error,
		clearError,
		clearData,
		callAPI
	} = useGetAPI({ endpoint });

	return {
		loadingNewCarriers: isLoading,
		newCarriers: data,
		getNewCarriersError: error,
		clearNewCarrierError: clearError,
		callGetNewCarriers: callAPI,
		clearNewCarrierData: clearData,
		apiEndpoint: endpoint,
	}
}

export const useFilterNewCarriers = () => {
	const {
		isLoading,
		data,
		error,
		clearError,
		clearData,
		callAPI
	} = useGetAPI({ endpoint: '/api/admin/inbox/carriers?{1}' });

	const triggerAPI = (query: string) => {
		callAPI({ routes: [query] });
	}

	return {
		loadingFilterNewCarriers: isLoading,
		filter: data,
		getFilteredNewCarriersError: error,
		clearFilterdCarrierError: clearError,
		clearFilterNewCarrierData: clearData,
		callGetFilterNewCarriers: triggerAPI,
	}
}

export const useActivateCarrier = () => {
	const {
		isLoading,
		data,
		error,
		clearError,
		clearData,
		callAPI
	} = usePatchAPI({ endpoint: '/api/admin/carrier/{1}/activate' });

	const triggerAPI = (id: string, payload: any) => {
		return callAPI({ routes: [id], payload });
	}

	return {
		loadingActivateCarrier: isLoading,
		activateCarrier: data,
		getActivateCarrierError: error,
		clearActivateCarrierError: clearError,
		clearActivateCarrierData: clearData,
		callActivateCarrier: triggerAPI
	}
}

export const useRejectCarrier = () => {
	const {
		isLoading,
		data,
		error,
		clearError,
		clearData,
		callAPI
	} = usePatchAPI({ endpoint: '/api/admin/carrier/reject-carrier' });

	const triggerAPI = (payload: any) => {
		return callAPI({ payload });
	}

	return {
		loadingRejectCarrier: isLoading,
		rejectCarrier: data,
		getRejectCarrierError: error,
		clearRejectCarrierError: clearError,
		clearRejectCarrierData: clearData,
		callRejectCarrier: triggerAPI
	}
}

export const useDeleteCarrier = () => {
	const {
		isLoading,
		data,
		error,
		clearError,
		clearData,
		callAPI
	} = usePatchAPI({ endpoint: '/api/admin/carrier/delete-carrier' });

	const triggerAPI = (payload: any) => {
		return callAPI({ payload });
	}

	return {
		loadingDeleteCarrier: isLoading,
		deleteCarrier: data,
		getDeleteCarrierError: error,
		clearDeleteCarrierError: clearError,
		clearDeleteCarrierData: clearData,
		callDeleteCarrier: triggerAPI
	}
}

/* 	ADMIN -> SHIPPER -> DASHBOARD */
export const useGetShipperDashboardQuotes = () => {
	const {
		isLoading,
		data,
		error,
		clearData,
		clearError,
		callAPI
	} = useGetAPI({ endpoint: '/api/admin/shipper/dashboard/quotes' });

	return {
		loadingQuotes: isLoading,
		quotes: data as { quotes: QuotesResponse; cancellationReasons: CancellationReason[] },
		quotesError: error as AJAXError,
		clearQuotesData: clearData,
		clearQuotesError: clearError,
		callGetQuotes: callAPI as (props?: APICallProps) => Promise<{
			data: { quotes: QuotesResponse; cancellationReasons: CancellationReason[] };
			error: AJAXError | null
		}>
	}
}
export const useGetShipperDashboardActiveShipments = () => {
	const {
		isLoading,
		data,
		error,
		clearData,
		clearError,
		callAPI
	} = useGetAPI({ endpoint: '/api/admin/shipper/dashboard/active-shipments' });

	return {
		loadingActiveShipments: isLoading,
		activeShipments: data as { activeShipments: TActiveShipmentPaginator; },
		activeShipmentsError: error as AJAXError,
		clearActiveShipmentsData: clearData,
		clearActiveShipmentsError: clearError,
		callGetActiveShipments: callAPI as (props?: APICallProps) => Promise<{
			data: { activeShipments: TActiveShipmentPaginator; };
			error: AJAXError | null
		}>
	}
}
export const useGetShipperDashboardNewShippers = () => {
	const {
		isLoading,
		data,
		error,
		clearData,
		clearError,
		callAPI
	} = useGetAPI({ endpoint: '/api/admin/shipper/dashboard/new-shippers' });

	return {
		loadingNewShippers: isLoading,
		newShippers: data as { newShippers: ShipperApprovals; },
		newShippersError: error as AJAXError,
		clearNewShippersData: clearData,
		clearNewShippersError: clearError,
		callGetNewShippers: callAPI as (props?: APICallProps) => Promise<{
			data: { newShippers: ShipperApprovals; };
			error: AJAXError | null
		}>
	}
}

// For Carrier Dashboard
export const useGetCarrierDashboardActiveManifests = () => {
	const {
		isLoading,
		data,
		error,
		clearData,
		clearError,
		callAPI
	} = useGetAPI({ endpoint: '/api/admin/carrier/dashboard/active-manifests' });

	return {
		loadingActiveManifests: isLoading,
		activeManifests: data as { activeManifests: TActiveManifest[]; },
		activeManifestsError: error as AJAXError,
		clearActiveManifestsData: clearData,
		clearActiveManifestsError: clearError,
		callGetActiveManifests: callAPI as (props?: APICallProps) => Promise<{
			data: { activeManifests: TActiveManifest[]; };
			error: AJAXError | null
		}>
	}
}
export const useGetCarrierDashboardNewCarriers = () => {
	const {
		isLoading,
		data,
		error,
		clearData,
		clearError,
		callAPI
	} = useGetAPI({ endpoint: '/api/admin/carrier/dashboard/new-carriers' });

	return {
		loadingNewCarriers: isLoading,
		newCarrier: data as { newCarrier: ShipperApprovals; },
		newCarrierError: error as AJAXError,
		clearNewCarrierData: clearData,
		clearNewCarrierError: clearError,
		callGetNewCarrier: callAPI as (props?: APICallProps) => Promise<{
			data: { unactivatedCarriers: TUnactivatedCarrier[]; };
			error: AJAXError | null
		}>
	}
}

export const useGetShipment = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({
		endpoint: '/admin/transport/transport-shipment/{1}',
		domain: 'Inertia'
	});

	const triggerAPI = (query: string) => {
		callAPI({ routes: [query] });
	}

	return {
		loadingShipmentData: isLoading,
		shipmentData: data?.data as TShipmentEOS,
		getShipmentDataError: error,
		callGetShipmentData: triggerAPI
	}
}

/* 	ADMIN --> FINANCE */
export const useGetBillingDetail = () => {
	const {
		isLoading,
		data,
		error,
		clearData,
		clearError,
		callAPI
	} = useGetAPI({ endpoint: '/api/admin/finance/{1}/billing/{2}/detail' });

	return {
		loadingBillingDetails: isLoading,
		billingDetails: data as TInvoiceDetails,
		billingDetailsError: error,
		clearBillingDetailsData: clearData,
		clearBillingDetailsError: clearError,
		callGetBillingDetails: callAPI as (props?: APICallProps) => Promise<{
			data: TInvoiceDetails;
			error: AJAXError | null
		}>
	}
}
export const useDownloadBillingInvoice = () => {
	const {
		isLoading,
		data,
		error,
		clearData,
		clearError,
		callAPI
	} = useGetAPI({ endpoint: '/api/admin/finance/{1}/billing/{2}/download' });

	return {
		loadingBillingInvoice: isLoading,
		billingInvoice: data as any,
		billingInvoiceError: error,
		clearBillingInvoiceData: clearData,
		clearBillingInvoiceError: clearError,
		callGetBillingInvoice: callAPI as (props?: APICallProps) => Promise<{
			data: any;
			error: AJAXError | null
		}>
	}
}
export const usePatchInvoiceStatus = () => {
	const {
		isLoading,
		data,
		error,
		clearData,
		clearError,
		callAPI
	} = usePatchAPI({ endpoint: '/api/admin/finance/invoice/{1}/status' });

	return {
		loadingInvoiceStatus: isLoading,
		invoiceStatus: data as TBillingInvoiceStatus,
		invoiceStatusError: error,
		clearInvoiceStatusData: clearData,
		clearInvoiceStatusError: clearError,
		callPatchInvoiceStatus: callAPI as (props?: APICallProps) => Promise<{
			data: TBillingInvoiceStatus;
			error: AJAXError | null
		}>
	}
}

/* 	ADMIN -> FINANCE -> FASTPAY BUDGET */
export const useGetFastPayBudget = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({ endpoint: '/api/admin/finance/fastpay-budget' });

	return {
		loadingBudget: isLoading,
		budgetData: data as any,
		budgetError: error,
		callGetFastPayBudget: callAPI
	}
}
export const usePatchFastPayBudget = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = usePatchAPI({ endpoint: '/api/admin/finance/fastpay-budget' });

	return {
		loadingPatchBudget: isLoading,
		patchBudgetData: data as any,
		patchBudgetError: error,
		callPatchFastPayBudget: callAPI
	}
}
export const useGetFastPayAuditLog = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({ endpoint: '/api/admin/finance/fastpay-budget/audit-log' });

	return {
		loadingAuditLog: isLoading,
		auditLogData: data as any,
		auditLogError: error,
		callGetAuditLog: callAPI
	}
}

/* 	ADMIN -> FINANCE -> SHIPPER BILLING & CARRIER BILLING */
export const useGetBillingIndex = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({ endpoint: '/api/admin/finance/{1}/billing' });

	return {
		loadingIndexBilling: isLoading,
		indexBilling: data as TBillingDataResponse,
		getIndexBillingError: error,
		callGetIndexBilling: callAPI as (props?: APICallProps) => Promise<TBillingDataResponse>
	}
}

export const useGetFilteredBillingIndex = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({ endpoint: '/api/admin/finance/{1}/billing?{2}' });

	const triggerAPI = (type: 'shipper' | 'carrier', search: string) => {
		return callAPI({ routes: [type, search] }) as Promise<TBillingDataResponse>;
	}

	return {
		loadingFilteredIndexBilling: isLoading,
		filteredIndexBilling: data as TBillingDataResponse,
		getFilteredIndexBillingError: error,
		callGetFilteredIndexBilling: triggerAPI
	}
}

export const useAssignTo = () => {
	const {
		isLoading,
		data,
		error,
		clearError,
		callAPI
	} = usePostAPI({ endpoint: '/api/admin/fulfilment/shipment/{1}/assign' });

	const triggerAPI = (shipment_id: number, payload: any) => {
		return callAPI({ routes: [`${shipment_id}`], payload });
	}

	return {
		loadingAssignTo: isLoading,
		assignedTo: data,
		assignedToError: error,
		clearAssignedToError: clearError,
		callAssignTo: triggerAPI
	}
}

export const useUnassignTo = () => {
	const {
		isLoading,
		data,
		error,
		clearError,
		callAPI
	} = usePostAPI({ endpoint: '/api/admin/fulfilment/shipment/{1}/unassign' });

	const triggerAPI = (shipment_id: number) => {
		return callAPI({ routes: [`${shipment_id}`] });
	}

	return {
		loadingUnassignTo: isLoading,
		unassignedTo: data,
		unassignedToError: error,
		clearUnassignedToError: clearError,
		callUnassignTo: triggerAPI
	}
}

export const useUpdateOrderNumber = () => {
	const {
		data,
		error,
		clearError,
		callAPI
	} = usePostAPI({ endpoint: '/api/admin/shipment/update-order-number' });

	return {
		updateOrderNumber: data,
		updateOrderNumberError: error as AJAXError,
		clearError,
		callUpdateOrderNumber: callAPI
	}
}

/* 	ADMIN -> FINANCE -> ISSUES AND CLAIMS */

export const useGetIssueAndClaims = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({ endpoint: '/api/admin/finance/claimLines' });

	return {
		loadingIssueAndClaims: isLoading,
		issueAndClaims: data?.rows as TIssueAndClaimTableRow[],
		accountableCount: data?.accountables as TIssueAndClaimAccountable,
		getIssueAndClaimsError: error,
		callIssueAndClaims: callAPI as (props?: APICallProps) => Promise<{
			data: {
				accountables: TIssueAndClaimAccountable
				total: number,
				rows: TIssueAndClaimTableRow[]
			}
			error: AJAXError | null
		}>,
	}
}

export const useClaimLinesBulkAction = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = usePatchAPI({ endpoint: '/api/admin/finance/claims/claimLines/bulkAction' });

	return {
		loadingBulkAction: isLoading,
		getBulkActionError: error,
		bulkActionResponse: data,
		callBulkAction: callAPI as (props?: APICallProps) => Promise<{
			data: { rows: TIssueAndClaimTableRow[] }
			error: AJAXError | null
		}>,
	}
}

export const useGetAccountables = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({ endpoint: '/api/admin/transport-shipment/accountables', });

	return {
		loadingAccountables: isLoading,
		accountables: data as TAccountable[],
		getAccountablesError: error,
		callAccountables: callAPI
	}
}

export const useGetClaimSubItems = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({ endpoint: '/api/admin/transport-shipment/claim-sub-items?claim_master_id={1}' });

	const triggerAPI = (claim_master_id: number) => {
		callAPI({ routes: [`${claim_master_id}`] });
	}
	return {
		loadingClaimSubItems: isLoading,
		claimSubItems: data as TSubClaim[],
		getClaimSubItemsError: error,
		callClaimSubItems: triggerAPI
	}
}

export const useGetClaimItems = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({ endpoint: '/api/admin/transport-shipment/claim-items' });

	return {
		loadingClaimItems: isLoading,
		claimItems: data as TClaimItem[],
		getClaimItemsError: error,
		callClaimItems: callAPI
	}
}

export const useFilterIssueAndClaims = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({ endpoint: '/api/admin/finance/claimLines' });

	return {
		loadingFilterIssueAndClaims: isLoading,
		filter: data?.rows as TIssueAndClaimTableRow[],
		getFilteredIssueAndClaimsError: error,
		callGetFilterIssueAndClaims: callAPI as (props?: APICallProps) => Promise<{
			data: {
				accountables: TIssueAndClaimAccountable,
				statuses: TIssueAndClaimStatuses,
				total: number,
				rows: TIssueAndClaimTableRow[]
			},
			error: AJAXError | null
		}>,
	}
}

/* 	ADMIN -> FINANCE -> FINANCE APPROVAL */

export const useGetFinanceApprovals = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({ endpoint: '/api/admin/finance/approvals' });

	return {
		loadingFinanceApprovals: isLoading,
		financeApprovals: data?.data as TFinanceApprovalRow[],
		financeApprovalsTotal: data?.meta?.total as number,
		getFinanceApprovalsError: error,
		callGetFinanceApprovals: callAPI as (props?: APICallProps) => Promise<{
			data: {
				data: TFinanceApprovalRow[],
				meta: { total: number }
			},
			error: AJAXError | null
		}>,
	}
}

export const useFinanceApprovalAction = () => {
	const {
		isLoading,
		error,
		callAPI
	} = usePostAPI({ endpoint: '/api/admin/finance/approvals/{1}/{2}' });

	return {
		loadingFinanceApprovalAction: isLoading,
		financeApprovalActionError: error,
		callFinanceApprovalAction: callAPI as (props?: APICallProps) => Promise<{
			data: { id: string, status: string },
			error: AJAXError | null
		}>,
	}
}

/* 	ADMIN -> FINANCE -> INVOICE REPORT */

export const useGetInvoices = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({ endpoint: '/api/admin/finance/invoice-audit' });

	return {
		loadingInvoices: isLoading,
		invoices: data as TInvoices,
		getInvoicesError: error,
		callGetInvoices: callAPI as (props?: APICallProps) => Promise<{
			data: TInvoices;
			error: AJAXError | null
		}>
	}
}

export const useRecheckInvoices = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = usePatchAPI({ endpoint: '/api/admin/finance/invoice-audit/{1}/recheck' });

	const triggerAPI = (id: number) => {
		return callAPI({ routes: [`${id}`] }) as Promise<{
			data: TRecheckInvoices,
			error: AJAXError | null
		}>;
	}

	return {
		loadingReCheckInvoices: isLoading,
		reCheckInvoices: data as TRecheckInvoices,
		getReCheckInvoicesError: error,
		callRecheckInvoices: triggerAPI
	}
}


export const useResolveInvoices = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = usePatchAPI({ endpoint: '/api/admin/finance/invoice-audit/{1}/resolve' });

	const triggerAPI = (id: number) => {
		return callAPI({ routes: [`${id}`] }) as Promise<{
			data: TRecheckInvoices;
			error: AJAXError | null
		}>;
	}

	return {
		loadingResolveInvoices: isLoading,
		resolveInvoices: data as TRecheckInvoices,
		getResolveInvoicesError: error,
		callResolveInvoices: triggerAPI
	}
}
export const useFilterInvoiceReport = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({ endpoint: '/api/admin/finance/invoice-audit?{1}' });

	const triggerAPI = (query: string) => {
		return callAPI({ routes: [query] }) as Promise<{
			data: TInvoices,
			error: AJAXError | null
		}>;
	};

	return {
		loadingFilterInvoiceReport: isLoading,
		filter: data?.rows as TInvoices,
		getFilteredInvoiceReportError: error,
		callGetFilterInvoiceReport: triggerAPI
	}
}

/* 	ADMIN -> FINANCE -> NON-RCTI INVOICES */
export const useGetNonRCTITableInvoices = () => {
	const endpoint = '/admin/finance/invoice/nonrcti';

	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({
		endpoint: `${endpoint}{1}`,
		domain: 'Inertia'
	});
	const triggerAPI = (query: string) => {
		callAPI({ routes: [query] });
	}
	return {
		loadingInvoices: isLoading,
		invoices: data as TNonRCTIInvoices,
		getInvoicesError: error,
		callGetInvoices: triggerAPI,
		apiEndpoint: endpoint
	}
}

export const useGetAllNonRCTITableInvoices = () => {
	const endpoint = '/admin/finance/invoice/all';

	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({
		endpoint: `${endpoint}{1}`,
		domain: 'Inertia'
	});
	const triggerAPI = (query: string) => {
		callAPI({ routes: [query] });
	}
	return {
		loadingAllInvoices: isLoading,
		allInvoices: data as TNonRCTIInvoices,
		getAllInvoicesError: error,
		callGetAllInvoices: triggerAPI,
		apiEndpoint: endpoint
	}
}

export const useGetNonRCTIInvoiceDetail = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({
		endpoint: '/admin/finance/invoice/{1}/details',
		domain: 'Inertia'
	});

	return {
		loadingInvoiceDetails: isLoading,
		invoiceDetails: data?.data as TNonRCTIInvoiceDetails,
		getInvoiceDetailsError: error,
		callGetInvoiceDetails: callAPI as (props?: APICallProps) => Promise<{
			data: {
				data: TNonRCTIInvoiceDetails;
				perPage: number;
				totalRows: number;
				currentPage: number;
			} | null;
			error: AJAXError | null
		}>
	}
}

export const useUpdateInvoiceStatus = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = usePostAPI({
		endpoint: '/admin/finance/invoice/status',
		domain: 'Inertia'
	});

	const triggerAPI = (payload: TInvoiceAction): Promise<{
		data: any;
		error: null
	} | undefined> => {
		return callAPI({ payload });
	}

	return {
		loadingUpdateInvoiceStatus: isLoading,
		updateInvoiceStatusData: data as TNonRCTIInvoiceDetails,
		getUpdateInvoiceStatusError: error,
		callUpdateInvoiceStatus: triggerAPI
	}
}

export const useAssignInvoice = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = usePostAPI({
		endpoint: '/admin/finance/invoice/{1}/assign',
		domain: 'Inertia'
	});
	const triggerAPI = (payload: {
		invoiceId: string,
		comments: string,
	}): Promise<{
		data: any;
		error: null
	} | undefined> => {
		return callAPI({ routes: [payload.invoiceId], payload });
	}
	return {
		loadingAssignInvoice: isLoading,
		assignInvoiceData: data as TNonRCTIInvoiceDetails,
		getAssignInvoiceError: error,
		callAssignInvoice: triggerAPI
	}
}

export const usePatchInvoiceMinorAdjustment = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = usePatchAPI({
		endpoint: '/admin/finance/invoice/adjustment',
		domain: 'Inertia'
	});
	const triggerAPI = (payload: TInvoiceMinorAdjustmentPayload) => {
		callAPI({ payload });
	}
	return {
		loadingPatchInvoiceMinorAdjustment: isLoading,
		patchInvoiceMinorAdjustment: data?.data as TNonRCTIInvoiceDetails,
		getPatchInvoiceMinorAdjustmentError: error,
		callPatchInvoiceMinorAdjustment: triggerAPI
	}
}

export const useGetInvoiceShipments = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({ endpoint: '/api/admin/finance/invoice?search={1}&carrierId={2}' });

	const triggerAPI = (searchValue: string, carrierId: string) => {
		callAPI({ routes: [searchValue, carrierId] });
	}

	return {
		loadingInvoiceShipments: isLoading,
		invoiceShipments: data?.data as TNonRCTIInvoiceShipment[],
		getInvoiceShipmentsError: error,
		callGetInvoiceShipments: triggerAPI
	}
}

export const useGetInvoiceShipmentSuggestions = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({ endpoint: '/api/admin/finance/invoice/{1}/suggestions' });

	const triggerAPI = (invoiceId: string) => {
		return callAPI({ routes: [invoiceId] });
	}

	return {
		loadingInvoiceSuggestions: isLoading,
		invoiceSuggestions: data?.data as TNonRCTIInvoiceShipment[],
		getInvoiceSuggestionsError: error,
		callGetInvoiceSuggestions: triggerAPI
	}
}

export const useMatchShipmentToInvoice = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = usePostAPI({ endpoint: '/api/admin/finance/invoice/match' });
	const triggerAPI = (payload: TMatchOrRejectShipmentPayload) => {
		return callAPI({ payload });
	}
	return {
		loadingMatchShipmentToInvoice: isLoading,
		matchShipmentToInvoiceData: data,
		getMatchShipmentToInvoiceError: error,
		callMatchShipmentToInvoice: triggerAPI
	}
}

export const useRejectShipmentSuggestion = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = usePostAPI({ endpoint: '/api/admin/finance/invoice/reject' });
	const triggerAPI = (payload: TMatchOrRejectShipmentPayload) => {
		callAPI({ payload });
	}
	return {
		loadingRejectShipmentSuggestion: isLoading,
		rejectShipmentSuggestionData: data,
		getRejectShipmentSuggestionError: error,
		callRejectShipmentSuggestion: triggerAPI
	}
}

export const useUnmatchShipment = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = usePostAPI({ endpoint: '/api/admin/finance/invoice/{1}/unmatch' });
	const triggerAPI = (invoiceLineID: number) => {
		return callAPI({ routes: [String(invoiceLineID)] });
	}
	return {
		loadingUnmatchShipment: isLoading,
		unmatchShipmentData: data,
		getUnmatchShipmentError: error,
		callUnmatchShipment: triggerAPI
	}
}

export const useGetRejectionReasons = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({
		endpoint: '/admin/finance/invoice/rejection-reasons',
		domain: 'Inertia'
	});
	return {
		loadingRejectionReasons: isLoading,
		rejectionReasonsData: data as string[],
		getRejectionReasonsError: error,
		callGetRejectionReasons: callAPI
	}
}

export const useGetAdjustmentReasons = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({
		endpoint: '/admin/finance/invoice/adjustment-reasons',
		domain: 'Inertia'
	});
	return {
		loadingAdjustmentReasons: isLoading,
		adjustmentReasonsData: data as string[],
		getAdjustmentReasonsError: error,
		callGetAdjustmentReasons: callAPI
	}
}

export const useDeleteInvoiceRow = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = useDeleteAPI({ endpoint: '/api/admin/finance/invoiceLine/{1}/delete' });

	const triggerAPI = (invoiceLineID: string): Promise<{
		data: any
		error: null
	} | undefined> => {
		return callAPI({ routes: [invoiceLineID] });
	}

	return {
		loadingDeleteInvoiceRow: isLoading,
		deleteInvoiceRowData: data,
		getDeleteInvoiceRowError: error,
		callDeleteInvoiceRow: triggerAPI
	}
}

export const useEditInvoiceRowTotal = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = usePatchAPI({ endpoint: '/api/admin/finance/invoiceLine/{1}/edit' });
	const triggerAPI = (invoiceLineID: string, newTotal: string) => {
		callAPI({
			routes: [String(invoiceLineID)],
			payload: { newTotal }
		});
	}
	return {
		loadingEditInvoiceTotal: isLoading,
		editInvoiceTotalData: data,
		getEditInvoiceTotalError: error,
		callEditInvoiceTotal: triggerAPI
	}
}

export const useEditInvoice = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = usePatchAPI({ endpoint: '/api/admin/finance/invoice/{1}/edit' });
	return {
		loadingEditInvoice: isLoading,
		editInvoiceData: data,
		getEditInvoiceError: error,
		callEditInvoice: callAPI as (props?: APICallProps) => Promise<{
			data: { details: TAdminCompanyDetails } | null;
			error: AJAXError | null
		}>
	}
}

export const useAddInvoiceRow = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = usePostAPI({ endpoint: '/api/admin/finance/invoice/{1}/add-line' });
	const triggerAPI = (payload: {
		invoiceId: string,
		referenceNumber: string;
		orderNumber: string;
		total: string
	}): Promise<{
		data: any;
		error: null
	} | undefined> => {
		return callAPI({
			routes: [String(payload.invoiceId)],
			payload
		});
	}
	return {
		loadingAddInvoiceRow: isLoading,
		addInvoiceRowData: data,
		getAddInvoiceRowError: error,
		callAddInvoiceRow: triggerAPI
	}
}

export const useAddCreditNote = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = usePostAPI({ endpoint: '/api/admin/finance/invoice/{1}/credit-note', });
	const triggerAPI = (invoiceRowId: string, payload: {
		creditNoteNumber: string;
		amount: string;
		documents: TDocument[];
	}): Promise<{
		data: {
			success: boolean;
		};
		error: AJAXError | null;
	} | undefined> => {
		return callAPI({ routes: [invoiceRowId], payload });
	}
	return {
		loadingAddCreditNote: isLoading,
		addCreditNoteData: data as {
			success: boolean;
		},
		getAddCreditNoteError: error,
		callAddCreditNote: triggerAPI
	}
}
/* --
	ADMIN -> ISSUES AND CLAIMS */

export const useGetShipmentIssuesAndClaims = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({
		endpoint: '/admin/transport/transport-shipment/{1}/claims',
		domain: 'Inertia'
	});

	const triggerAPI = (query: string) => {
		callAPI({ routes: [query] });
	}

	return {
		loadingshipmentIssuesAndClaims: isLoading,
		shipmentIssuesAndClaims: data?.data as TIssueAndClaimItem[],
		getShipmentIssuesAndClaimsError: error,
		callGetShipmentIssuesAndClaims: triggerAPI
	}
}

export const useGetClaimAccountables = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({
		endpoint: '/admin/transport/accountables',
		domain: 'Inertia'
	});

	return {
		loadingClaimAccountables: isLoading,
		claimAccountables: data as TAccountable[],
		getClaimAccountablesError: error,
		callGetClaimAccountables: callAPI
	}
}


/* 	ADMIN -> CONFIGURATION -> COMPANY */
export const useGetCompanyDetails = () => {
	const {
		isLoading,
		data,
		error,
		clearData,
		clearError,
		callAPI
	} = useGetAPI({ endpoint: '/api/admin/configuration/company' });

	return {
		loadingCompanyDetails: isLoading,
		companyDetails: data as { details: TAdminCompanyDetails },
		getCompanyDetailsError: error as AJAXError,
		clearGetCompanyDetailsData: clearData,
		clearGetCompanyDetailsError: clearError,
		callGetCompanyDetails: callAPI as (props?: APICallProps) => Promise<{
			data: { details: TAdminCompanyDetails } | null;
			error: AJAXError | null
		}>
	}
}

export const useUpdateCompanyDetails = () => {
	const {
		isLoading,
		data,
		error,
		clearData,
		clearError,
		callAPI
	} = usePostAPI({ endpoint: '/api/admin/configuration/company' });

	return {
		loadingUpdateCompanyDetails: isLoading,
		updateCompanyDetails: data as { type: string; message: string },
		updateCompanyDetailsError: error as AJAXError,
		clearUpdateCompanyDetailsData: clearData,
		clearUpdateCompanyDetailsError: clearError,
		callUpdateCompanyDetails: callAPI as (props?: APICallProps) => Promise<{
			data: { type: string; message: string } | null;
			error: AJAXError | null
		}>
	}
}

export const useDeleteCompanyDetails = () => {
	const {
		isLoading,
		data,
		error,
		clearData,
		clearError,
		callAPI
	} = usePostAPI({ endpoint: '/api/admin/configuration/companydelete' });

	return {
		loadingDeleteCompanyDetails: isLoading,
		deleteCompanyDetails: data as { type: string; message?: string },
		deleteCompanyDetailsError: error as AJAXError,
		clearDeleteCompanyDetailsData: clearData,
		clearDeleteCompanyDetailsError: clearError,
		callDeleteCompanyDetails: callAPI as (props?: APICallProps) => Promise<{
			data: { type: string; message?: string } | null;
			error: AJAXError | null
		}>
	}
}

export const useGetCompanyTeam = () => {
	const {
		isLoading,
		data,
		error,
		clearData,
		clearError,
		callAPI
	} = useGetAPI({ endpoint: '/api/admin/team' });

	return {
		loadingCompanyTeam: isLoading,
		companyTeam: data as { data: TAdminTeamResponse },
		getCompanyTeamError: error as AJAXError,
		clearGetCompanyTeamData: clearData,
		clearGetCompanyTeamError: clearError,
		callGetCompanyTeam: callAPI as (props?: APICallProps) => Promise<{
			data: { data: TAdminTeamResponse } | null;
			error: AJAXError | null
		}>
	}
}

export const usePostResetPassword = () => {
	const {
		isLoading,
		data,
		error,
		clearData,
		clearError,
		callAPI
	} = usePostAPI({ endpoint: '/api/admin/admin-reset-password' });

	return {
		loadingResetPassword: isLoading,
		resetPassword: data as { type: string; message: string },
		resetPasswordError: error as AJAXError,
		clearResetPasswordData: clearData,
		clearResetPasswordError: clearError,
		callResetPassword: callAPI as (props?: APICallProps) => Promise<{
			data: { type: string; message: string } | null;
			error: AJAXError | null
		}>
	}
}

/* SHIPMENT -> COMPANY -> DEACTIVATE */
export const useDeactivateShipper = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = usePatchAPI({ endpoint: '/api/admin/shipper/{1}/shipper-deactivate' });

	return {
		loadingDeactivateShipper: isLoading,
		deactivateShipperError: error as AJAXError,
		deactivateShipperResponse: data,
		callDeactivateShipper: callAPI as (props?: APICallProps) => Promise<{
			data: { type: string; message: string } | null;
			error: AJAXError | null
		}>
	}
}

/* 	ADMIN -> CONFIGURATION -> TRUCK TYPE */
export const useGetTruckTypes = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({ endpoint: '/api/admin/configuration/trucktypes' });

	return {
		loadingTruckType: isLoading,
		truckType: data?.trucktype as TTruckType[],
		getTruckTypeError: error,
		callGetTruckType: callAPI
	}
}

export const useGetTruckSize = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({ endpoint: '/api/admin/configuration/trucktypes' });

	return {
		loadingTruckSize: isLoading,
		truckSize: data?.trucksize as TTruckSize[],
		getTruckSizeError: error,
		callGetTruckSize: callAPI
	}
}

/* 	ADMIN -> CONFIGURATION -> REGISTERED CARRIERS */

export const useGetRegisteredCarriers = () => {
	const endpoint = '/api/admin/configuration/registered-carriers';
	const {
		isLoading,
		data,
		error,
		clearError,
		clearData,
		callAPI
	} = useGetAPI({ endpoint });

	return {
		loadingRegisteredCarriers: isLoading,
		registeredCarriers: data as TRegisteredCarrier,
		getRegisteredCarriersError: error,
		clearRegisteredCarrierError: clearError,
		callGetRegisteredCarriers: callAPI as (props?: APICallProps) => Promise<{
			data: TRegisteredCarrier;
			error: AJAXError | null
		}>,
		clearRegisteredData: clearData,
		apiEndpoint: endpoint,
	}
}

export const useGetRegisteredShipper = () => {
	const endpoint = '/api/admin/configuration/registered-shippers';
	const {
		isLoading,
		data,
		error,
		clearError,
		clearData,
		callAPI
	} = useGetAPI({ endpoint });

	return {
		loadingRegisteredShipper: isLoading,
		registeredShipper: data as TRegisteredShipper,
		getRegisteredShipperError: error as AJAXError,
		clearRegisteredShipperError: clearError,
		callGetRegisteredShipper: callAPI as (props?: APICallProps) => Promise<{
			data: TRegisteredShipper;
			error: AJAXError | null
		}>,
		clearRegisteredData: clearData,
		apiEndpoint: endpoint,
	}
}

/* 	ADMIN -> CONFIGURATION -> MARKETPLACE */

export const useMarketPlace = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({ endpoint: '/api/admin/configuration/marketplace' });

	return {
		loadingMarketPlace: isLoading,
		marketPlaces: data as TMarketPlaces,
		getMarketPlaceError: error,
		callGetMarketPlaces: callAPI
	}
}

export const useUpdateMarketPlace = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = usePatchAPI({ endpoint: '/api/admin/marketplaceRuleUpdate' });
	const triggerAPI = (payload: TRule) => {
		callAPI({ payload });
	}
	return {
		loadingUpdateMarketPlace: isLoading,
		updateMarketPlaces: data as TMarketPlaceHoliday,
		getUpdateMarketPlaceError: error,
		callUpdateMarketPlaces: triggerAPI
	}
}

export const useCreateMarketPlaceHoliday = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = usePostAPI({ endpoint: '/api/admin/marketplaceHolidayCreate' });
	const triggerAPI = (payload: THolidays) => {
		callAPI({ payload });
	}
	return {
		loadingCreateMarketPlaceHoliday: isLoading,
		createMarketPlaceHoliday: data as TMarketPlaceHoliday,
		getCreateMarketPlaceHolidayError: error,
		callCreateMarketPlaceHoliday: triggerAPI
	}
}

export const useUpdateMarketPlaceHoliday = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = usePatchAPI({ endpoint: '/api/admin/marketplaceHolidayUpdate' });
	const triggerAPI = (payload: THolidays) => {
		callAPI({ payload });
	}
	return {
		loadingUpdateMarketPlaceHoliday: isLoading,
		updateMarketPlaceHoliday: data as TMarketPlaceHoliday,
		getUpdateMarketPlaceHolidayError: error,
		callUpdateMarketPlaceHoliday: triggerAPI
	}
}

export const useDeleteMarketPlaceHoliday = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = useDeleteAPI({ endpoint: '/api/admin/marketplaceHolidayDelete' });
	const triggerAPI = (payload: THolidays) => {
		callAPI({ payload });
	}
	return {
		loadingDeleteMarketPlaceHoliday: isLoading,
		deleteMarketPlaceHoliday: data as TMarketPlaceHoliday,
		getDeleteMarketPlaceHolidayError: error,
		callDeleteMarketPlaceHoliday: triggerAPI
	}
}
export const useGetShipperList = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({ endpoint: '/api/admin/shippers' });
	return {
		loadingShipperList: isLoading,
		shipperList: data?.data as TShipperList,
		getShipperListError: error,
		callGetShipperList: callAPI
	}
}

export const useGetCarrierList = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({
		endpoint: '/admin/carriers',
		domain: 'Inertia'
	});
	return {
		loadingCarrierList: isLoading,
		carrierList: data?.data as TCarrier[],
		getCarrierListError: error,
		callGetCarrierList: callAPI
	}
}

export const useAssignShipments = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = usePostAPI({ endpoint: '/api/admin/fulfilment/shipment/bulk-assign' });

	return {
		assignShipmentsIsLoading: isLoading,
		assignShipmentsError: error,
		callAssignShipments: (shipmentIds: number[], assigneeId: string) => {
			return callAPI({
				payload: {
					shipments: shipmentIds,
					assignee: assigneeId,
				}
			})
		},
		assigningShipmentsData: data
	}
}

export const usePostCargoAdjustment = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = usePostAPI({ endpoint: '/api/admin/transport-shipment/pallet-adjustment' });

	return {
		loadingCargoAdjustment: isLoading,
		cargoAdjustmentData: data?.data as TCargoAdjustmentResponse,
		getCargoAdjustmentError: error,
		callGetCargoAdjustment: callAPI as (props?: APICallProps) => Promise<{
			data: TCargoAdjustmentResponse;
			error: AJAXError | null;
		}>
	}
}

export const useGetInvoiceHistory = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({
		endpoint: '/admin/finance/invoice/{1}/log',
		domain: 'Inertia'
	});

	const triggerCallAPI = (id: string) => {
		callAPI({ routes: [id] });
	}
	return {
		loadingInvoiceHistory: isLoading,
		invoiceHistory: data?.data as TInvoiceHistory[],
		getInvoiceHistoryError: error,
		callGetInvoiceHistory: triggerCallAPI
	}
}

export const usePostInvoiceComment = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = usePostAPI({
		endpoint: '/admin/finance/invoice/{1}/comment',
		domain: 'Inertia'
	});
	const triggerAPI = (invoiceId: string, payload: TInvoiceCommentPayload) => {
		callAPI({ routes: [invoiceId], payload });
	}
	return {
		loadingPostInvoiceComment: isLoading,
		postInvoiceCommentData: data as TNonRCTIInvoiceDetails,
		getPostInvoiceCommentError: error,
		callPostInvoiceComment: triggerAPI
	}
}

export const useDownloadInvoice = () => {
	const {
		data,
		error,
		callAPI
	} = useGetAPI({
		endpoint: '/admin/finance/invoice/{1}/download',
		domain: 'Inertia'
	});

	const triggerAPI = (query: string) => {
		callAPI({ routes: [query] });
	}

	return {
		downloadInvoice: data as TInvoiceDownload,
		callGetDownloadInvoice: triggerAPI,
		getDownloadInvoiceError: error
	}
}

export const usePostInvoice = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = usePostAPI({
		endpoint: '/admin/finance/invoice/upload',
		headers: { 'Content-Type': 'multipart/form-data', },
		domain: 'Inertia'
	});

	const triggerAPI = (payload: FormData) => {
		callAPI({ payload });
	}

	return {
		loadingPostInvoice: isLoading,
		postInvoiceData: data as any,
		getPostInvoiceError: error,
		callPostInvoice: triggerAPI
	}
}

/* @deprecated, favor useGetAdminUsers */
export const useGetAdminTeam = () => {
	const {
		isLoading,
		data,
		error,
		clearData,
		clearError,
		callAPI
	} = useGetAPI({ endpoint: '/api/admin/team/full-list' });

	return {
		loadingAdminTeam: isLoading,
		adminTeam: data as TAdminTeamMember[],
		getAdminTeamError: error,
		clearGetAdminTeamData: clearData,
		clearGetAdminTeamError: clearError,
		callGetAdminTeam: callAPI as (props?: APICallProps) => Promise<{
			data: TAdminTeamMember[];
			error: AJAXError | null;
		}>
	}
}

export const useGetAdminUsers = () => {
	const {
		isLoading,
		data,
		error,
		clearData,
		clearError,
		callAPI
	} = useGetAPI({ endpoint: '/api/admin/users' });

	return {
		loadingAdminUsers: isLoading,
		users: data as { data: TUserBrief[] },
		getAdminTeamError: error,
		clearGetAdminUsersData: clearData,
		clearGetAdminUsersError: clearError,
		callGetAdminUsers: callAPI as (props?: APICallProps) => Promise<{
			data: { data: TUserBrief[] };
			error: AJAXError | null;
		}>
	}
}

export const useGetQuotePricePreview = () => {
	const customerID = useRecoilValue(XCustomerID);
	const headers = customerID ? { 'X-Customer-Id': `${customerID}` } : undefined;
	const {
		isLoading,
		data,
		error,
		callAPI
	} = usePostAPI({
		endpoint: '/api/lanes/price-preview',
		domain: 'ShipmentAPI',
		headers,
	});

	return {
		isLoadingQuotePricePreview: isLoading,
		quotePricePreview: data?.data as PricePreviewResponse,
		getQuotePricePreviewError: error,
		callGetQuotePricePreview: callAPI as (props?: APICallProps) => Promise<{
			data: {
				data: PricePreviewResponse | null;
			};
			error: AJAXError | null
		}>
	}
}

export const useRejectQuote = () => {
	const {
		isLoading,
		data,
		error,
		clearError,
		clearData,
		callAPI
	} = usePatchAPI({ endpoint: '/api/admin/inbox/quote/{1}/reject' });

	return {
		loadingRejectQuote: isLoading,
		rejectQuote: data,
		getRejectQuoteError: error,
		clearRejectQuoteError: clearError,
		clearRejectQuoteData: clearData,
		callRejectQuote: callAPI as (props?: NotQuotingPayloadAPICallProps) => Promise<{
			data: {
				data: any;
			};
			error: AJAXError | null
		}>
	}
}

export const useDownloadDocument = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({ endpoint: '/api/admin/transport/documents/{1}/download' })

	return {
		loadingDocuments: isLoading,
		documents: data as any,
		documentError: error,
		callGetDocuments: callAPI
	}
}

export const usePostPriceAdjustment = () => {
	const {
		isLoading,
		data,
		error,
		callAPI,
		clearError,
	} = usePostAPI({ endpoint: '/api/admin/transport-shipment/price-adjustment/{1}' });

	return {
		loadingPriceAdjustment: isLoading,
		priceAdjustment: data?.data as TPriceAdjustment,
		priceAdjustmentError: error as AJAXError,
		clearPriceAdjustmentError: clearError,
		callPriceAdjustment: callAPI as (props?: APICallProps) => Promise<{
			data: { data: TPriceAdjustment };
			error: AJAXError | null
		}>
	}
}

export const usePostModifierAdjustment = () => {
	const {
		isLoading,
		data,
		error,
		callAPI,
		clearError,
	} = usePostAPI({ endpoint: '/api/admin/transport-shipment/modifier-adjustment/{1}' });

	return {
		loadingModifierAdjustment: isLoading,
		modifierAdjustment: data?.data as TPriceAdjustment,
		modifierAdjustmentError: error as AJAXError,
		clearModifierAdjustmentError: clearError,
		callModifierAdjustment: callAPI as (props?: APICallProps) => Promise<{
			data: { data: TPriceAdjustment };
			error: AJAXError | null
		}>
	}
}

/* 	Configuration --> Regions */

export const useGetRegions = () => {
	const {
		isLoading,
		data,
		error,
		clearError,
		clearData,
		callAPI
	} = useGetAPI({
		endpoint: '/api/region',
		domain: 'ShipmentAPI'
	});

	return {
		loadingRegions: isLoading,
		regions: data as { data: TRegionBase[] },
		getRegionsError: error,
		clearRegionsError: clearError,
		clearRegionsData: clearData,
		callGetRegions: callAPI as (props?: APICallProps) => Promise<{
			data: { data: TRegionBase[] };
			error: AJAXError | null
		}>
	}
}

export const useGetRegion = () => {
	const {
		isLoading,
		data,
		error,
		clearError,
		clearData,
		callAPI
	} = useGetAPI({
		endpoint: '/api/region/{1}',
		domain: 'ShipmentAPI'
	});

	return {
		loadingRegion: isLoading,
		region: data as { data: IRegionItem },
		getRegionError: error,
		clearRegionError: clearError,
		clearRegionData: clearData,
		callGetRegion: callAPI as (props?: APICallProps) => Promise<{
			data: { data: IRegionItem };
			error: AJAXError | null
		}>
	}
}

export const useGetRegionAddress = () => {
	const {
		isLoading,
		data,
		error,
		clearError,
		clearData,
		callAPI
	} = useGetAPI({
		endpoint: '/api/postcode',
		domain: 'ShipmentAPI'
	});

	return {
		loadingRegionAddresses: isLoading,
		regionAddresses: data as { data: TRegionAddressItem[] },
		getRegionAddressesError: error,
		clearRegionAddressesError: clearError,
		clearRegionAddressesData: clearData,
		callGetRegionAddresses: callAPI as (props?: APICallProps) => Promise<{
			data: { data: TRegionAddressItem[] };
			error: AJAXError | null
		}>
	}
}

export const useCreateRegion = () => {
	const {
		isLoading,
		data,
		error,
		clearError,
		clearData,
		callAPI
	} = usePostAPI({
		endpoint: '/api/region',
		domain: 'ShipmentAPI'
	});

	return {
		loadingCreateRegion: isLoading,
		createRegion: data as any,
		createRegionError: error,
		clearCreateRegionError: clearError,
		clearCreateRegionData: clearData,
		callCreateRegion: callAPI as (props?: APICallProps) => Promise<{
			data: any;
			error: AJAXError | null
		}>
	}
}

export const useUpdateRegion = () => {
	const {
		isLoading,
		data,
		error,
		clearError,
		clearData,
		callAPI
	} = usePatchAPI({
		endpoint: '/api/region/{1}',
		domain: 'ShipmentAPI'
	});

	return {
		loadingUpdateRegion: isLoading,
		updateRegion: data as any,
		updateRegionError: error,
		clearUpdateRegionError: clearError,
		clearUpdateRegionData: clearData,
		callUpdateRegion: callAPI as (props?: APICallProps) => Promise<{
			data: any;
			error: AJAXError | null
		}>
	}
}

export const useDeleteRegion = () => {
	const {
		isLoading,
		data,
		error,
		clearError,
		clearData,
		callAPI
	} = useDeleteAPI({
		endpoint: '/api/region/{1}',
		domain: 'ShipmentAPI'
	});

	return {
		loadingDeleteRegion: isLoading,
		deleteRegion: data as { message: string },
		deleteRegionError: error,
		clearDeleteRegionError: clearError,
		clearDeleteRegionData: clearData,
		callDeleteRegion: callAPI as (props?: APICallProps) => Promise<{
			data: { message: string };
			error: AJAXError | null
		}>
	}
}

export const useGetPolygon = (customerId: string | null = null) => {
	const headers = customerId ? { 'X-Customer-Id': customerId } : undefined;
	const {
		isLoading,
		data,
		error,
		clearError,
		clearData,
		callAPI
	} = useGetAPI({
		endpoint: '/api/geojson',
		domain: 'ShipmentAPI',
		headers
	});

	return {
		loadingPolygon: isLoading,
		polygon: data as { data: TGMapRegion | TGMapPostcodePolygon | TGMapPostcodePolygon[] | TGMapPostcodePolygon[][] },
		getPolygonError: error,
		clearPolygonError: clearError,
		clearPolygonData: clearData,
		callGetPolygon: callAPI as (props?: APICallProps) => Promise<{
			data: { data: TGMapRegion | TGMapPostcodePolygon | TGMapPostcodePolygon[] | TGMapPostcodePolygon[][] };
			error: AJAXError | null
		}>
	}
}

export const usePostRegionPolygon = () => {
	const {
		isLoading,
		data,
		error,
		clearError,
		clearData,
		callAPI
	} = usePostAPI({
		endpoint: '/api/region/{1}/geojson',
		domain: 'ShipmentAPI'
	});

	return {
		loadingPostPolygon: isLoading,
		postPolygon: data as any,
		getPostPolygonError: error,
		clearPostPolygonError: clearError,
		clearPostPolygonData: clearData,
		callPostPolygon: callAPI as (props?: APICallProps) => Promise<{
			data: any;
			error: AJAXError | null
		}>
	}
}

export const useGetRegionPolygon = () => {
	const {
		isLoading,
		data,
		error,
		clearError,
		clearData,
		callAPI
	} = useGetAPI({
		endpoint: '/api/region/{1}/geojson',
		domain: 'ShipmentAPI'
	});

	const triggerAPI = (regionId: string) => {
		return callAPI({ routes: [regionId] });
	}

	return {
		loadingRegionPolygon: isLoading,
		regionPolygon: data as { data: TGMapRegion; locality_type: string },
		getRegionPolygonError: error,
		clearRegionPolygonError: clearError,
		clearRegionPolygonData: clearData,
		callGetRegionPolygon: triggerAPI as (regionId: string) => Promise<{
			data: { data: TGMapRegion; locality_type: string };
			error: AJAXError | null
		}>
	}
}

export const useGetRegionUsedInRateCards = () => {
	const { callAPI } = useGetAPI({
		endpoint: `/api/region/{1}/exists-in-rate-card`,
		domain: 'ShipmentAPI',
	});

	return {
		callGetRegionUsedInRateCard: callAPI as (props?: APICallProps) => Promise<{
			data: {
				data: boolean
			},
			error: AJAXError | null
		}>
	}
}

export const useUploadCustomerRegions = () => {
	const customerID = useRecoilValue(XCustomerID);
	const headers: Record<string, string> = { 'Content-Type': 'multipart/form-data' };
	if (customerID) headers['X-Customer-Id'] = customerID;
	const {
		isLoading,
		data,
		error,
		clearError,
		callAPI
	} = usePostAPI({
		endpoint: `/api/region/customer/upload`,
		domain: 'ShipmentAPI',
		headers,
	});

	const triggerAPI = (file: File) => {
		const formData = new FormData();
		formData.append('file', file);
		return callAPI({ payload: formData });
	};

	return {
		loadingUpload: isLoading,
		uploadData: data as any,
		uploadError: error,
		clearUploadError: clearError,
		callUploadCustomerRegions: triggerAPI,
	};
}

export const useGetUploadCustomerRegionStatus = () => {
	const customerID = useRecoilValue(XCustomerID);
	const headers = customerID ? { 'X-Customer-Id': `${customerID}` } : undefined;
	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({
		endpoint: `/api/region/customer/upload/{1}`,
		domain: 'ShipmentAPI',
		headers,
	});

	return {
		loadingUploadStatus: isLoading,
		uploadStatusData: data as any,
		uploadStatusError: error,
		callGetUploadStatus: callAPI as (props?: APICallProps) => Promise<{ data: any; error: AJAXError | null }>,
	};
}

export const useGetShipmentCancellationReasons = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({ endpoint: '/api/shipment-cancelled-reasons' })

	return {
		loadingCancellationReasons: isLoading,
		cancellationReasons: data as { data: TShipmentCancellationReasons },
		cancellationReasonsError: error,
		callGetCancellationReasons: callAPI as (props?: APICallProps) => Promise<{
			data: { data: TShipmentCancellationReasons[] };
			error: AJAXError | null
		}>
	}
}

export const useGetShipmentOrder = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({ endpoint: '/api/admin/transport-shipment/shipment/{1}' })

	return {
		loadingOrder: isLoading,
		order: data,
		orderError: error as AJAXError,
		callGetShipmentOrder: callAPI as (props?: APICallProps) => Promise<{
			data: TShipmentEOS,
			error: AJAXError | null
		}>
	}

}

export const useAdminDeleteShipment = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = useDeleteAPI({ endpoint: '/api/admin/transport-shipment/{1}' });

	return {
		isLoading,
		adminDeleteShipment: data as string,
		adminDeleteShipmentError: error as AJAXError,
		callAdminDeleteShipment: callAPI as (props?: APICallProps) => Promise<{
			data: string,
			error: AJAXError | null
		}>
	}
}

export const useGetCostCenter = () => {
	const {
		data,
		isLoading,
		callAPI
	} = useGetAPI({ endpoint: `/api/admin/shipper/cost-centers`, });

	return {
		isLoading: isLoading,
		shipperList: data?.shippers as TShipperListOverview[],
		callGetCostCenter: callAPI as (props?: APICallProps) => Promise<{
			data: TShipperListOverview[],
			error: AJAXError | null
		}>
	}
}

export const useGetShipmentTagsList = () => {
	const {
		data,
		error,
		isLoading,
		callAPI
	} = useGetAPI({
		endpoint: `/api/shipper-tag?include=lanesCount`,
		domain: 'ShipmentAPI'
	});
	const triggerAPI = async (query: URLSearchParams | null = null) => {
		const apiParams = query ? { params: query } : {};
		await callAPI(apiParams);
	}

	return {
		isLoadingTags: isLoading,
		shipmentTagsList: data as TShipperTagResponse,
		tagsError: error,
		callGetShipmentTags: triggerAPI,
	}
}


export const useSaveShipperTag = () => {
	const {
		isLoading,
		error,
		callAPI
	} = usePostAPI({
		endpoint: '/api/shipper-tag',
		domain: 'ShipmentAPI'
	});

	const triggerAPI = async (payload: any) => {
		await callAPI({ payload });
	}

	return {
		loadingSave: isLoading,
		saveError: error,
		callSaveShipperTag: triggerAPI
	}
}

export const useUpdateShipperTag = () => {
	const { isLoading, error, callAPI } = usePutAPI({
		endpoint: '/api/shipper-tag/{1}',
		domain: 'ShipmentAPI'
	});

	const triggerAPI = async (shipperTag: any, payload: any) => {
		await callAPI({
			routes: [shipperTag],
			payload,
		});
	}

	return {
		loadingUpdate: isLoading,
		updateError: error,
		callUpdateShipperTag: triggerAPI
	}
}

export const useGetSingleShipperTag = () => {
	const {
		data,
		isLoading,
		callAPI
	} = useGetAPI({
		endpoint: '/api/shipper-tag/{1}',
		domain: 'ShipmentAPI'
	});

	const triggerAPI = async (shipperTagId: string) => {
		await callAPI({ routes: [shipperTagId] });
	}

	return {
		loadingSingleTag: isLoading,
		shipperTag: data?.data as TShipperTag,
		callGetSingleShipperTag: triggerAPI
	}
}


export const useDeletShipperTag = () => {
	const {
		isLoading,
		callAPI,
		error
	} = useDeleteAPI({
		endpoint: '/api/shipper-tag/{1}',
		domain: 'ShipmentAPI'
	});

	const triggerAPI = async (shipperTagId: string) => {
		await callAPI({ routes: [shipperTagId] });
	}

	return {
		loadingDelete: isLoading,
		deleteError: error,
		callDeleteShipperTag: triggerAPI
	}
}

export const useGetDataBulkUploads = () => {
	const endpoint = '/api/admin/configuration/data-bulk-upload';
	const {
		isLoading,
		data,
		callAPI,
		error
	} = useGetAPI({ endpoint });

	return {
		loadingGetDataBulkUploads: isLoading,
		getDataBulkUploadsError: error,
		DataBulkUploadsData: data?.data,
		callGetDataBulkUploads: callAPI,
		apiEndpoint: endpoint,
	}
}

export const usePostDataBulkUpload = () => {
	const {
		isLoading,
		data,
		callAPI,
		error
	} = usePostAPI({ endpoint: '/api/admin/configuration/data-bulk-upload?type={1}&required_auto_approve={2}', });

	const triggerAPI = async (type: string, requiredAutoApprove: string, payload: FormData) => {
		console.log(type, requiredAutoApprove, payload)
		await callAPI({
			routes: [type, requiredAutoApprove],
			payload: payload
		});
	}

	return {
		loadingPostDataBulkUpload: isLoading,
		postDataBulkUploadError: error,
		postDataBulkUploadData: data,
		callpostDataBulkUpload: triggerAPI
	}
}

export const useDownloadDataBulkUploadResult = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({ endpoint: '/api/admin/configuration/data-bulk-upload/{1}/download', });

	return {
		downLoading: isLoading,
		downloadDataBulkUploadResult: data as any,
		callDownloadDataBulkUploadResult: callAPI as (props?: APICallProps) => Promise<{
			data: any;
			error: AJAXError | null
		}>,
		downloadDataBulkUploadResultError: error
	}
}

export const useGenerateToken = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = usePostAPI({ endpoint: '/api/admin/inbound/integration/regenerate' });

	return {
		isLoadingTokenData: isLoading,
		tokenData: data,
		error: error,
		callGenerateToken: callAPI as (props?: APICallProps) => Promise<{
			data: TToken,
			error: AJAXError | null
		}>,
	};
};

export const useSaveToken = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = usePostAPI({ endpoint: '/api/admin/inbound/integration/generate' });

	return {
		isSavingTokenData: isLoading,
		tokenData: data,
		error: error,
		callSaveToken: callAPI as (props?: APICallProps) => Promise<{
			data: { is_connected: boolean },
			error: AJAXError | null
		}>,
	};
}

export const useDeleteToken = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = useDeleteAPI({ endpoint: '/api/admin/inbound/integration/disable' });

	return {
		isLoadingTokenData: isLoading,
		tokenData: data,
		error: error,
		callDeleteToken: callAPI as (props?: APICallProps) => Promise<{
			data: { is_connected: boolean },
			error: AJAXError | null
		}>,
	};
}

export const usePostTransportQuery = () => {
	const {
		isLoading,
		data,
		error,
		clearData,
		clearError,
		callAPI
	} = usePostAPI({ endpoint: '/api/typesense/multisearch' });

	return {
		loadingTransportQuery: isLoading,
		transportQuery: data as { results: ITypeSense<any>[] },
		transportQueryError: error as AJAXError,
		clearTransportQueryData: clearData,
		clearTransportQueryError: clearError,
		callTransportQueryAPI: callAPI as (props?: APICallProps) => Promise<{
			data: { results: ITypeSense<any>[] };
			error: AJAXError | null
		}>
	}
}

/**
 * Hook for sending Terms and Conditions link via email
 * @returns Object containing loading state, response data, error, and function to call the API
 */
export const useSendTNCLink = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = usePostAPI({ endpoint: '/api/admin/onboard/send-terms-conditions/{1}/{2}' });


	return {
		loadingSendTNCLink: isLoading,
		tncLinkResponse: data,
		sendTNCLinkError: error,
		callSendTNCLink: callAPI as (props?: APICallProps) => Promise<{
			data: any | null;
			error: AJAXError | null
		}>
	};
};

/* ADMIN -> CARRIER BLOCKS */

export const useGetCarrierBlocks = () => {
	const sID = useRecoilValue(SID);
	const headers = sID ? { 'X-Shipper-Token': `${sID}` } : undefined;

	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({ endpoint: '/api/shipper/carrier-blocks', headers });

	return {
		loadingCarrierBlocks: isLoading,
		carrierBlocks: data as TCarrierBlocksResponse | null,
		carrierBlocksError: error,
		callGetCarrierBlocks: callAPI as (props?: APICallProps) => Promise<{
			data: TCarrierBlocksResponse | null;
			error: AJAXError | null;
		}>
	};
};

export const useCreateCarrierBlock = () => {
	const sID = useRecoilValue(SID);
	const headers = sID ? { 'X-Shipper-Token': `${sID}` } : undefined;

	const {
		isLoading,
		data,
		error,
		callAPI
	} = usePostAPI({ endpoint: '/api/shipper/carrier-blocks', headers });

	return {
		loadingCreateCarrierBlock: isLoading,
		createCarrierBlockResponse: data as TCarrierBlock | null,
		createCarrierBlockError: error,
		callCreateCarrierBlock: callAPI as (props?: APICallProps) => Promise<{
			data: TCarrierBlock | null;
			error: AJAXError | null;
		}>
	};
};

export const useUpdateCarrierBlock = () => {
	const sID = useRecoilValue(SID);
	const headers = sID ? { 'X-Shipper-Token': `${sID}` } : undefined;

	const {
		isLoading,
		data,
		error,
		callAPI
	} = usePutAPI({ endpoint: '/api/shipper/carrier-blocks/{1}', headers });

	return {
		loadingUpdateCarrierBlock: isLoading,
		updateCarrierBlockResponse: data as TCarrierBlock | null,
		updateCarrierBlockError: error,
		callUpdateCarrierBlock: callAPI as (props?: APICallProps) => Promise<{
			data: TCarrierBlock | null;
			error: AJAXError | null;
		}>
	};
};

export const useDeleteCarrierBlock = () => {
	const sID = useRecoilValue(SID);
	const headers = sID ? { 'X-Shipper-Token': `${sID}` } : undefined;

	const {
		isLoading,
		data,
		error,
		callAPI
	} = useDeleteAPI({ endpoint: '/api/shipper/carrier-blocks/{1}', headers });

	return {
		loadingDeleteCarrierBlock: isLoading,
		deleteCarrierBlockResponse: data,
		deleteCarrierBlockError: error,
		callDeleteCarrierBlock: callAPI as (props?: APICallProps) => Promise<{
			data: any | null;
			error: AJAXError | null;
		}>
	};
};

export const useCheckCarrierBlock = () => {
	const sID = useRecoilValue(SID);
	const headers = sID ? { 'X-Shipper-Token': `${sID}` } : undefined;
	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({ endpoint: '/api/shipper/carrier-blocks/check', headers });

	return {
		loadingCheckCarrierBlock: isLoading,
		checkCarrierBlockResponse: data as TCarrierBlockCheckResponse | null,
		checkCarrierBlockError: error,
		callCheckCarrierBlock: callAPI as (props?: APICallProps) => Promise<{
			data: TCarrierBlockCheckResponse | null;
			error: AJAXError | null;
		}>
	};
};

export const useGetCarrierBlockReasons = () => {
	const sID = useRecoilValue(SID);
	const headers = sID ? { 'X-Shipper-Token': `${sID}` } : undefined;

	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({ endpoint: '/api/shipper/carrier-blocks/reasons', headers });

	return {
		loadingCarrierBlockReasons: isLoading,
		carrierBlockReasons: data as TBlockReasonOption[] | null,
		carrierBlockReasonsError: error,
		callGetCarrierBlockReasons: callAPI as (props?: APICallProps) => Promise<{
			data: TBlockReasonOption[] | null;
			error: AJAXError | null;
		}>
	};
};

export const useGetCarrierBlockCarriers = () => {
	const sID = useRecoilValue(SID);
	const headers = sID ? { 'X-Shipper-Token': `${sID}` } : undefined;

	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({ endpoint: '/api/shipper/carrier-blocks/carriers', headers });

	return {
		loadingCarrierBlockCarriers: isLoading,
		carrierBlockCarriers: data as any[] | null,
		carrierBlockCarriersError: error,
		callGetCarrierBlockCarriers: callAPI as (props?: APICallProps) => Promise<{
			data: TCarrierBlockOptions[] | null;
			error: AJAXError | null;
		}>
	};
};

export const useGetABNLookup = () => {
	const guid = import.meta.env.VITE_REACT_GUID ?? 'NA';
	const [isLoading, setIsLoading] = useState(false);

	const callGetABNLookup = useCallback((props?: APICallProps) => {
		return new Promise<{ data: TABNResponse | null; error: AJAXError | null }>((resolve) => {
			const abn = props?.routes?.[0];
			if (!abn) return resolve({ data: null, error: { message: 'ABN required' } as AJAXError });

			setIsLoading(true);

			(window as any).abnResponse = (data: TABNResponse) => {
				setIsLoading(false);
				if (!data || data.AbnStatus !== 'Active') {
					resolve({ data: null, error: { message: data?.Message || 'ABN not found or inactive' } as AJAXError });
				} else {
					resolve({ data, error: null });
				}
			};

			const existingScript = document.getElementById('abn-jsonp-script');
			if (existingScript) existingScript.remove();

			const script = document.createElement('script');
			script.id = 'abn-jsonp-script';
			script.src = `https://abr.business.gov.au/json/AbnDetails.aspx?callback=abnResponse&abn=${abn}&guid=${guid}`;
			document.body.appendChild(script);
		});
	}, [guid]);

	return {
		loadingABNLookup: isLoading,
		callGetABNLookup
	}
}

export const useGetEmailLookup = () => {
	const sID = useRecoilValue(SID);
	const headers = sID ? { 'X-Shipper-Token': `${sID}` } : undefined;

	const {
		isLoading,
		callAPI
	} = usePostAPI({ endpoint: '/api/email-check', headers });

	return {
		loadingEmailCheck: isLoading,
		callGetEmailLookup: callAPI as (props?: APICallProps) => Promise<{
			data: TEmailCheck | null;
			error: AJAXError | null;
		}>
	}
}

export const useAdminUploadPOD = () => {
	const {
		isLoading,
		data,
		error,
		callAPI,
	} = usePostAPI({ endpoint: '' });

	const uploadPOD = async (
		manifestId: string,
		formData: FormData,
		options?: Partial<APICallProps>
	): Promise<{ data: any; error: AJAXError | null }> => {
		const res = await callAPI({
			payload: formData,
			overrideUrl: `/api/admin/manifests/${manifestId}/pod`,
			headers: {
				'Content-Type': 'multipart/form-data',
				...options?.headers,
			},
			...options,
		});

		if (!res || res.error) throw res?.error ?? new Error('Unknown error');

		return res.data;
	};

	return {
		uploadingPOD: isLoading,
		uploadedData: data,
		uploadError: error,
		uploadPOD,
	};
};

export const useAdminDeletePOD = () => {
	const {
		isLoading,
		callAPI,
	} = useDeleteAPI({ endpoint: '/api/admin/transport-shipment/podDocument/{1}' });

	return {
		deletePODLoading: isLoading,
		callDeletePOD: callAPI as (props?: APICallProps) => Promise<{
			data: any;
			error: AJAXError | null;
		}>,
	};
};

export const useAdminApprovePOD = () => {
	const {
		isLoading,
		callAPI
	} = usePostAPI({ endpoint: '/api/admin/transport-shipment/proof-of-delivery/accept' });

	return {
		loadingApprovePOD: isLoading,
		callAdminApprovePOD: callAPI as (props?: APICallProps) => Promise<{
			data: any;
			error: AJAXError | null;
		}>
	}
}

export const useAdminRejectPOD = () => {
	const {
		isLoading,
		callAPI
	} = usePostAPI({ endpoint: '/api/admin/transport-shipment/proof-of-delivery/reject' });

	return {
		loadingRejectPOD: isLoading,
		callAdminRejectPOD: callAPI as (props?: APICallProps) => Promise<{
			data: any;
			error: AJAXError | null;
		}>
	}
}

export const useAdminUpdatePODstatus = () => {
	const {
		isLoading,
		callAPI
	} = usePostAPI({ endpoint: '/api/admin/transport-shipment/{1}/update-pod-status' });

	return {
		loadingUpdatePODstatus: isLoading,
		callUpdatePODstatus: callAPI as (props?: APICallProps) => Promise<{
			data: any;
			error: AJAXError | null;
		}>
	}
}

export const useAdminPatchCargo = () => {
	const {
		isLoading,
		callAPI
	} = usePatchAPI({ endpoint: '/api/admin/transport-shipment/cargo/{1}' });

	return {
		loadingUpdateCargo: isLoading,
		callUpdateCargo: callAPI as (props?: APICallProps) => Promise<{
			data: any;
			error: AJAXError | null;
		}>
	}
}

export const usePDFtoImage = () => {
	const {
		isLoading,
		callAPI,
	} = usePostAPI({ endpoint: '/api/admin/transport-shipment/{1}/get-pdf-to-image' });

	return {
		pdfToImageLoading: isLoading,
		callPDFtoImage: callAPI as (props?: APICallProps) => Promise<{
			data: {
				images: {
					fileName: string;
					id: number;
					image_id: number;
					uri: string;
				}[];
				message: string;
				type: string;
			};
			error: AJAXError | null;
		}>,
	};
};

export const useGetNoteCarrierOptions = () => {
	const {
		isLoading,
		callAPI
	} = useGetAPI({ endpoint: '/api/admin/shipnotes/carrier-options/{1}' });

	return {
		loadingEmailCheck: isLoading,
		callGetNoteCarrierOptions: callAPI as (props?: APICallProps) => Promise<{
			data: any;
			error: AJAXError | null;
		}>
	}
}

export const useAdminGetShipperNotes = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({ endpoint: '/api/admin/shipnotes' });

	return {
		adminLoadingNotes: isLoading,
		adminNotes: data as TShipmentNote[],
		adminNotesError: error,
		callAdminGetShipperNotes: callAPI as (props?: APICallProps) => Promise<{
			data: {
				data: TShipmentNote[],
				links: Link[],
				meta: {
					total: number,
					per_page: number,
				}
			},
			error: AJAXError | null
		}>
	}
}

export const usePostShipperNote = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = usePostAPI({ endpoint: '/api/admin/shipnotes' });

	return {
		loadingNote: isLoading,
		note: data as any,
		noteError: error,
		callPostShipperNote: callAPI as (props?: APICallProps) => Promise<{
			data: any,
			error: AJAXError | null
		}>
	}
}

export const usePostBulkShipperNotes = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = usePostAPI({ endpoint: '/api/admin/shipnotes/bulk-create' });

	return {
		loadingBulkNotes: isLoading,
		bulkNotes: data as any,
		bulkNotesError: error,
		callPostBulkShipperNotes: callAPI as (props?: APICallProps) => Promise<{
			data: any,
			error: AJAXError | null
		}>
	}
}

export const useAdminUpdateShipperNotes = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = usePatchAPI({ endpoint: '/api/admin/shipnotes/update' });

	return {
		adminUpdateLoadingNotes: isLoading,
		adminUpdateNotes: data as TShipmentNote,
		adminUpdateNotesError: error,
		callAdminUpdateShipperNotes: callAPI as (props?: APICallProps) => Promise<{
			data: {
				data: TShipmentNote
			},
			error: AJAXError | null
		}>
	}
}

export const useAdminDeleteShipperNotes = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = useDeleteAPI({ endpoint: '/api/admin/shipnotes/{1}' });

	return {
		adminDeleteLoadingNotes: isLoading,
		adminDeleteNotes: data as any,
		adminDeleteNotesError: error,
		callAdminDeleteShipperNotes: callAPI as (props?: APICallProps) => Promise<{
			data: any,
			error: AJAXError | null
		}>
	}
}


export const useGetAdminETAReasons = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({ endpoint: '/api/admin/eta-reasons' })

	return {
		loadingETAReasons: isLoading,
		etaReasons: data as { data: ETAReason[] },
		etaReasonsError: error as AJAXError,
		callGetAdminETAReasons: callAPI as (props?: APICallProps) => Promise<{
			data: { data: ETAReason[] },
			error: AJAXError | null
		}>
	}
}

export const useGetMissedTargetOptions = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({ endpoint: '/api/missed-target-reasons' })

	return {
		loadingMissedTargetOptions: isLoading,
		missedTargetOptions: data as TMissedTargetOptionsResponse,
		missedTargetOptionsError: error as AJAXError,
		callGetMissedTargetOptions: callAPI as (props?: APICallProps) => Promise<{
			data: TMissedTargetOptionsResponse,
			error: AJAXError | null
		}>
	}
}

export const useUpdateMissedTarget = () => {
	const {
		isLoading,
		callAPI
	} = usePostAPI({ endpoint: '/api/cargo/{1}/update-missed-target' })

	return {
		loadingUpdateMissedTarget: isLoading,
		callUpdateMissedTarget: callAPI as (props?: APICallProps) => Promise<{
			data: TMissedTargetUpdateResponse,
			error: AJAXError | null
		}>
	}
}

export const useAdminGetShipperOrder = () => {
	const {
		isLoading,
		data,
		error,
		clearError,
		clearData,
		callAPI
	} = useGetAPI({ endpoint: '/api/admin/transport/transport-shipment/{1}' });

	return {
		loadingOrder: isLoading,
		order: data as { data: TShipmentOrder },
		getOrderError: error as AJAXError,
		clearGetOrderError: clearError,
		clearGetOrderData: clearData,
		callGetOrder: callAPI as (props?: APICallProps) => Promise<{
			data: { data: TShipmentOrder };
			error: AJAXError | null
		}>
	}
}

export const useDownloadTransportDocs = () => {
	const {
		isLoading,
		data,
		error,
		clearError,
		clearData,
		callAPI
	} = useGetAPI({ endpoint: '/api/admin/transport/documents/{1}/download' });

	return {
		loadingDocs: isLoading,
		docs: data as string,
		getDocsError: error as AJAXError,
		clearGetDocsError: clearError,
		clearGetDocsData: clearData,
		callGetDocs: callAPI as (props?: APICallProps) => Promise<{
			data: string;
			error: AJAXError | null
		}>
	}
}

export const usePatchTimeslotBookingState = () => {
	const {
		isLoading,
		callAPI
	} = usePatchAPI({ endpoint: '/api/admin/transport-shipment/{1}/time-slot-booking-state' });

	return {
		loadingPatchTSBookingState: isLoading,
		callPatchTSBookingState: callAPI as (props?: APICallProps) => Promise<{
			data: any;
			error: AJAXError | null
		}>
	}
}

export const useGetGlobalFuelLevies = () => {
	const {
		isLoading,
		callAPI
	} = useGetAPI(
		{
			endpoint: '/api/fuel-levy/global?includeTariffs=true&type={1}',
			domain: 'ShipmentAPI'
		}
	);

	return {
		loadingGlobalFuelLevies: isLoading,
		callGetGlobalFuelLevies: callAPI as (props?: APICallProps) => Promise<{
			data: { data: TFuelLevy } | null;
			error: AJAXError | null;
		}>,
	}
}

export const useCreateGlobalFuelLevyTariff = () => {
	const {
		isLoading,
		callAPI
	} = usePostAPI(
		{
			endpoint: '/api/fuel-levy/global/{1}/tariff',
			domain: 'ShipmentAPI'
		}
	);

	return {
		creatingGlobalFuelLevyTariff: isLoading,
		callCreateGlobalFuelLevyTariff: callAPI as (props?: APICallProps) => Promise<{
			data: { data: TFuelLevyTariff } | null;
			error: AJAXError | null;
		}>,
	}
}

export const useResendAllocationEmail = () => {
	const {
		isLoading,
		callAPI
	} = usePostAPI(
		{ endpoint: '/api/admin/transport-shipment/{1}/resend-allocation-email' }
	);

	return {
		resendEmailLoading: isLoading,
		callResendAllocationEmail: callAPI as (props?: APICallProps) => Promise<{
			data: { type: string; message: string };
			error: AJAXError | null;
		}>,
	}
}

export const useGetUnassignReasons = () => {
	const {
		isLoading,
		callAPI
	} = useGetAPI(
		{ endpoint: '/api/admin/transport-shipment/carrier-unassign-reasons' }
	);

	return {
		unassignReasonsLoading: isLoading,
		callGetUnassignReasons: callAPI as (props?: APICallProps) => Promise<{
			data: {
				data: TCarrierUnassignReason[]
			};
			error: AJAXError | null;
		}>,
	}
}

export const useUnallocateCarrier = () => {
	const {
		isLoading,
		callAPI
	} = usePatchAPI(
		{ endpoint: '/api/admin/transport-shipment/{1}/unallocate' }
	);

	return {
		unallocateCarrierLoading: isLoading,
		callUnallocateCarrier: callAPI as (props?: APICallProps) => Promise<{
			data: {
				type: string;
				message: string;
				meta?: {
					redirect: boolean;
					job_id: string | number;
				}
			};
			error: AJAXError | null;
		}>,
	}
}

export const useGetAdminShipmentHistory = () => {
	const {
		isLoading,
		callAPI
	} = useGetAPI(
		{ endpoint: '/api/admin/transport-shipment/{1}/history' }
	);

	return {
		getShipmenthistoryLoading: isLoading,
		callGetShipmentHistory: callAPI as (props?: APICallProps) => Promise<{
			data: {
				data: TShipmentHistory[];
				message: string;
				type: string;
			};
			error: AJAXError | null;
		}>,
	}
}

export const useResendHistoryEmail = () => {
	const {
		isLoading,
		callAPI
	} = usePostAPI(
		{ endpoint: '/api/admin/transport-shipment/{1}/resend-email-event', }
	);

	return {
		resendEmailLoading: isLoading,
		callResendHistoryEmail: callAPI as (props?: APICallProps) => Promise<{
			data: any;
			error: AJAXError | null;
		}>,
	}
}

export const useGetShipmentDocs = () => {
	const {
		isLoading,
		callAPI
	} = useGetAPI({ endpoint: '/api/admin/transport-shipment/shipment-docs' });

	return {
		loadingShipmentDocs: isLoading,
		callGetShipmentDocs: callAPI as (props?: APICallProps) => Promise<{
			data: { data: TShipmentDocs[] };
			error: AJAXError | null;
		}>,
	}
}

export const useGetClaims = () => {
	const {
		isLoading,
		callAPI
	} = useGetAPI({ endpoint: '/api/admin/transport-shipment/{1}/claims' });

	return {
		loadingClaims: isLoading,
		callGetClaims: callAPI as (props?: APICallProps) => Promise<{
			data: { data: TIssueAndClaimItem[] };
			error: AJAXError | null;
		}>
	}
}

export const useSubmitNewClaim = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = usePostAPI({ endpoint: '/api/admin/transport-shipment/{1}/claims' });

	const triggerAPI = (obj: {
		shipmentId: number,
		payload: {
			claim_lines: TNewClaimPayload[]
		}
	}) => {
		return callAPI({ routes: [`${obj.shipmentId}`], payload: obj.payload });
	}

	return {
		loadingSubmitNewClaim: isLoading,
		submitNewClaimData: data,
		submitNewClaimError: error,
		callSubmitNewClaim: triggerAPI
	}
}

export const useGetCarrierBidsAndRecommendations = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({ endpoint: '/api/admin/fulfilment/unallocated/job-list/{1}/bids-and-recommendations' });

	return {
		loadingRecommendations: isLoading,
		recommendations: data as THarmonyResponse | null,
		recommendationsError: error as AJAXError,
		callGetRecommendations: callAPI as (props?: APICallProps) => Promise<{
			data: THarmonyResponse;
			error: AJAXError | null;
		}>,
	}
}

export const useAllocateCarrier = () => {
	const {
		isLoading,
		callAPI
	} = usePatchAPI(
		{ endpoint: '/api/admin/fulfilment/call-centres/{1}/carriers/{2}' }
	);

	return {
		allocateCarrierLoading: isLoading,
		callAllocateCarrier: callAPI as (props?: APICallProps) => Promise<{
			data: { message: string };
			error: AJAXError | null;
		}>,
	}
}

export const useGetMarginNegativeReasons = () => {
	const {
		isLoading,
		callAPI,
	} = useGetAPI({ endpoint: '/api/admin/fulfilment/margin-negative-reasons' });

	return {
		loadingMarginNegativeReasons: isLoading,
		callGetMarginNegativeReasons: callAPI as (props?: APICallProps) => Promise<{
			data: { reasons: Array<{ label: string; value: string }> };
			error: AJAXError | null;
		}>,
	};
};

export const useGetPodToBeAssigned = () => {
	const {
		isLoading,
		callAPI,
	} = useGetAPI({ endpoint: '/api/admin/transport/proof-of-delivery/to-be-assigned' });

	return {
		loadingPodToBeAssigned: isLoading,
		callGetPodToBeAssigned: callAPI as (props?: APICallProps) => Promise<{
			data: {
				data: TPodToBeAssigned[];
				meta?: {
					current_page: number;
					per_page: number;
					total: number;
					last_page: number;
				};
			};
			error: AJAXError | null;
		}>,
	};
};

export const useAssignPod = () => {
	const {
		isLoading,
		callAPI,
	} = usePostAPI({ endpoint: '/api/admin/transport/transport-shipment/proof-of-delivery/assign' });

	return {
		assignPodLoading: isLoading,
		callAssignPod: callAPI as (props?: APICallProps) => Promise<{
			data: { message: string };
			error: AJAXError | null;
		}>,
	};
};

export const useDeletePodToBeAssigned = () => {
	const {
		isLoading,
		callAPI,
	} = useDeleteAPI({ endpoint: '/api/admin/transport/transport-shipment/proof-of-delivery/delete/{1}' });

	return {
		deletePodLoading: isLoading,
		callDeletePod: callAPI as (props?: APICallProps) => Promise<{
			data: any;
			error: AJAXError | null;
		}>,
	};
};

export const useGenerateDocumentUrl = () => {
	const {
		isLoading,
		callAPI,
	} = useGetAPI({ endpoint: '/api/admin/transport/documents/{1}/generate-url' });

	return {
		generateUrlLoading: isLoading,
		callGenerateDocumentUrl: callAPI as (props?: APICallProps) => Promise<{
			data: string;
			error: AJAXError | null;
		}>,
	};
};

export const useSearchTransportShipments = () => {
	const {
		isLoading,
		callAPI,
	} = usePostAPI({ endpoint: '/api/admin/transport/transport-shipment/search' });

	return {
		searchLoading: isLoading,
		callSearchShipments: callAPI as (props?: APICallProps) => Promise<{
			data: { data: TShipmentSearchResult[] };
			error: AJAXError | null;
		}>,
	};
};

export const useEOSFetchShipments = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = usePostAPI({ endpoint: '/api/admin/transport/transport-shipment' });

	return {
		isLoadingShipments: isLoading,
		shipmentsData: data as TDataGridResponse<TTransportShipment>,
		shipmentsError: error,
		fetchShipments: callAPI as (props?: APICallProps) => Promise<{
			data: TDataGridResponse<TTransportShipment>;
			error: AJAXError | null;
		}>
	};
};

export type TLocationImportStatus = 'pending' | 'complete' | 'failed';

export type TLocationImportOutcome =
	| 'created'
	| 'matched_existing_location'
	| 'duplicate_in_file'
	| 'failed_blank_address'
	| 'failed_invalid_hours'
	| 'failed_geocoding'
	| 'failed_saving';

export interface ILocationImportRowResult {
	row: number;
	name: string | null;
	address: string | null;
	outcome: TLocationImportOutcome;
	reason: string | null;
}

export type ILocationImportSummary = Record<TLocationImportOutcome, number> & { total: number };

export interface ILocationImportResult {
	status: TLocationImportStatus;
	output: string | null;
	dry_run: boolean;
	summary: ILocationImportSummary | null;
	results: ILocationImportRowResult[] | null;
}

export const usePostLocationImportValidate = () => {
	const {
		isLoading,
		data,
		callAPI,
		error
	} = usePostAPI({ endpoint: '/api/admin/shipper/{1}/locations/import/validate' });

	const triggerAPI = (shipperId: number, file: File) => {
		const formData = new FormData();
		formData.append('file', file);
		return callAPI({ routes: [`${shipperId}`], payload: formData });
	};

	return {
		loadingLocationImportValidate: isLoading,
		locationImportValidateError: error,
		locationImportValidateData: data as { job_id: string } | null,
		callPostLocationImportValidate: triggerAPI
	};
};

export const useGetLocationImportStatus = () => {
	const {
		isLoading,
		data,
		callAPI,
		error
	} = useGetAPI({ endpoint: '/api/admin/shipper/locations/import/{1}' });

	return {
		loadingLocationImportStatus: isLoading,
		locationImportStatusError: error,
		locationImportStatusData: data as ILocationImportResult | null,
		callGetLocationImportStatus: callAPI as (props?: APICallProps) => Promise<{
			data: ILocationImportResult;
			error: AJAXError | null;
		}>
	};
};

export const usePostLocationImportConfirm = () => {
	const {
		isLoading,
		data,
		callAPI,
		error
	} = usePostAPI({ endpoint: '/api/admin/shipper/{1}/locations/import/{2}/confirm' });

	const triggerAPI = (shipperId: number, jobId: string) => {
		return callAPI({ routes: [`${shipperId}`, jobId] });
	};

	return {
		loadingLocationImportConfirm: isLoading,
		locationImportConfirmError: error,
		locationImportConfirmData: data as { job_id: string } | null,
		callPostLocationImportConfirm: triggerAPI
	};
};

export const useSendToMarketplace = () => {
	const {
		isLoading,
		callAPI,
	} = usePatchAPI({ endpoint: '/api/admin/fulfilment/job-list/{1}/process-to-marketplace' });

	return {
		sendToMarketplaceLoading: isLoading,
		callSendToMarketplace: callAPI as (props?: APICallProps) => Promise<{
			data: { success: boolean; message: string };
			error: AJAXError | null;
		}>,
	};
};

export const useSendToUnallocated = () => {
	const {
		isLoading,
		callAPI,
	} = usePatchAPI({ endpoint: '/api/admin/fulfilment/job-list/{1}/process-to-unallocated' });

	return {
		sendToUnallocatedLoading: isLoading,
		callSendToUnallocated: callAPI as (props?: APICallProps) => Promise<{
			data: { success: boolean; message: string };
			error: AJAXError | null;
		}>,
	};
};

export const useAcceptBid = () => {
	const {
		isLoading,
		callAPI,
	} = usePatchAPI({ endpoint: '/api/admin/fulfilment/marketplace-bid/{1}/accept' });

	return {
		acceptBidLoading: isLoading,
		callAcceptBid: callAPI as (props?: APICallProps) => Promise<{
			data: { type: string; message: string };
			error: AJAXError | null;
		}>,
	};
};

export const useRejectBid = () => {
	const {
		isLoading,
		callAPI,
	} = usePatchAPI({ endpoint: '/api/admin/fulfilment/marketplace-bid/{1}/reject' });

	return {
		rejectBidLoading: isLoading,
		callRejectBid: callAPI as (props?: APICallProps) => Promise<{
			data: { type: string; message: string };
			error: AJAXError | null;
		}>,
	};
};

/* 	ADMIN -> CARRIER -> COMPANY PROFILE */
export const useGetCarrierCompanyDetails = () => {
	const cID = useRecoilValue(CID);
	const authUser = useRecoilValue(authenticatedUser);
	const carrierToken = cID && cID > 0 ? cID : authUser?.carrier?.id;
	const {
		isLoading,
		data,
		error,
		clearData,
		clearError,
		callAPI
	} = useGetAPI({
		endpoint: '/api/carrier/profile',
		headers: { 'X-Carrier-Token': `${carrierToken}` }
	});

	return {
		loadingCarrierCompanyDetails: isLoading,
		carrierCompanyDetails: data as { details: TAdminCompanyDetails },
		getCarrierCompanyDetailsError: error as AJAXError,
		clearCarrierCompanyDetailsData: clearData,
		clearCarrierCompanyDetailsError: clearError,
		callGetCarrierCompanyDetails: callAPI as (props?: APICallProps) => Promise<{
			data: { details: TAdminCompanyDetails } | null;
			error: AJAXError | null
		}>
	}
}

export const useUpdateCarrierCompanyDetails = () => {
	const cID = useRecoilValue(CID);
	const authUser = useRecoilValue(authenticatedUser);
	const carrierToken = cID && cID > 0 ? cID : authUser?.carrier?.id;
	const {
		isLoading,
		data,
		error,
		clearData,
		clearError,
		callAPI
	} = usePatchAPI({
		endpoint: '/api/carrier/profile',
		headers: { 'X-Carrier-Token': `${carrierToken}` }
	});

	return {
		loadingUpdateCarrierCompanyDetails: isLoading,
		updateCarrierCompanyDetails: data as { type: string; message: string },
		updateCarrierCompanyDetailsError: error as AJAXError,
		clearUpdateCarrierCompanyDetailsData: clearData,
		clearUpdateCarrierCompanyDetailsError: clearError,
		callUpdateCarrierCompanyDetails: callAPI as (props?: APICallProps) => Promise<{
			data: { type: string; message: string } | null;
			error: AJAXError | null
		}>
	}
}

export const useGetShipmentMisses = () => {
	const {
		isLoading,
		callAPI
	} = useGetAPI({ endpoint: '/api/admin/difot/shipments/{1}/misses' });

	return {
		loadingGetMissRecords: isLoading,
		callGetMissRecords: callAPI as (props?: APICallProps) => Promise<{
			data: { data: { sifot: TShipmentMissesGroup; difot: TShipmentMissesGroup } };
			error: AJAXError | null;
		}>
	};
};

export const useGetDifotReasons = () => {
	const {
		isLoading,
		data,
		callAPI
	} = useGetAPI({ endpoint: '/api/admin/difot/reasons' });

	return {
		loadingGetDifotReasons: isLoading,
		difotReasons: (data as { data: TDifotReason[] } | null)?.data ?? null,
		callGetDifotReasons: callAPI as (props?: APICallProps) => Promise<{
			data: { data: TDifotReason[] };
			error: AJAXError | null;
		}>
	};
};

export const usePatchSifotDifotStatus = () => {
	const {
		isLoading,
		callAPI
	} = usePatchAPI({ endpoint: '/api/admin/difot/shipments/{1}/misses/{2}' });

	return {
		loadingPatchSifotDifotStatus: isLoading,
		callPatchSifotDifotStatus: callAPI as (props?: APICallProps) => Promise<{
			data: any;
			error: AJAXError | null
		}>
	}
};

export const usePostNominateMiss = () => {
	const {
		isLoading,
		callAPI
	} = usePostAPI({ endpoint: '/api/admin/difot/shipments/{1}/misses/{2}/nominate' });

	return {
		loadingNominateMiss: isLoading,
		callNominateMiss: callAPI as (props?: APICallProps) => Promise<{
			data: any;
			error: AJAXError | null
		}>
	}
};

export const useGetDifotReasonsByPillar = () => {
	const {
		isLoading,
		callAPI
	} = useGetAPI({ endpoint: '/api/admin/difot/reasons' });

	return {
		loadingGetDifotReasonsByPillar: isLoading,
		callGetDifotReasonsByPillar: callAPI as (props?: APICallProps) => Promise<{
			data: { data: TDifotReason[] };
			error: AJAXError | null;
		}>
	}
};